import { resolveApiUrl } from "./apiBase";

export async function getBugLog() {
  const res = await fetch("/BUGS.md");
  if (!res.ok) throw new Error("Failed to fetch bug log");
  return await res.text();
}
export const API_URL = resolveApiUrl();

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // FastAPI validation errors are typically [{ loc, msg, type }, ...]
    const parts = detail
      .map((it) => {
        if (it == null) return '';
        if (typeof it === 'string') return it;
        const msg = it?.msg != null ? String(it.msg) : '';
        const loc = Array.isArray(it?.loc) ? it.loc.join('.') : (it?.loc != null ? String(it.loc) : '');
        return [loc, msg].filter(Boolean).join(': ');
      })
      .filter(Boolean);
    return parts.join('; ');
  }
  if (typeof detail === 'object') {
    if (detail?.msg != null) return String(detail.msg);
    try { return JSON.stringify(detail); } catch { return String(detail); }
  }
  return String(detail);
}

export async function getProjects(params = {}) {
  const usp = new URLSearchParams();
  if (params?.include_system) usp.set('include_system', '1');
  const url = `${API_URL}/projects/${usp.toString() ? `?${usp.toString()}` : ''}`;
  const res = await fetch(url, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error("Failed to fetch projects");
  return await res.json();
}

export async function getCurrentTenant() {
  const res = await fetch(`${API_URL}/tenants/current`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Kunde inte hämta tenant';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function patchCurrentTenantSettings(body) {
  const res = await fetch(`${API_URL}/tenants/current/settings`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara inställningar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Tenant email config ---

export async function getTenantEmailConfig() {
  const res = await fetch(`${API_URL}/tenants/current/email-config`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta e-postkonfiguration';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function patchTenantEmailConfig(body) {
  const res = await fetch(`${API_URL}/tenants/current/email-config`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara e-postkonfiguration';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function sendTestEmail(body) {
  const res = await fetch(`${API_URL}/tenants/current/test-email`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skicka testmail';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Tenant SMS config ---

export async function getTenantSmsConfig() {
  const res = await fetch(`${API_URL}/tenants/current/sms-config`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta SMS-konfiguration';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function sendTestSms(body) {
  const res = await fetch(`${API_URL}/tenants/current/test-sms`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skicka test-SMS';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Tenant departments (Medarbetarenkät m.fl.) ---

export async function getTenantDepartments() {
  const res = await fetch(`${API_URL}/tenants/current/departments`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta avdelningar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function patchTenantDepartments(departments) {
  const res = await fetch(`${API_URL}/tenants/current/departments`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify({ departments: departments || [] }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara avdelningar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Chart of Accounts (kontoplan per tenant) ---

export async function listChartOfAccounts(params = {}) {
  const qs = new URLSearchParams();
  if (params.includeInactive) qs.append('include_inactive', 'true');
  if (params.accountClass) qs.append('account_class', params.accountClass);
  if (params.q) qs.append('q', params.q);
  const url = `${API_URL}/chart-of-accounts${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Kunde inte hämta kontoplanen';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function listChartOfAccountClasses() {
  const res = await fetch(`${API_URL}/chart-of-accounts/classes`, {
    credentials: 'include', headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Kunde inte hämta kontoklasser');
  return await res.json();
}

export async function createChartOfAccount(body) {
  const res = await fetch(`${API_URL}/chart-of-accounts`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa konto';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function updateChartOfAccount(id, body) {
  const res = await fetch(`${API_URL}/chart-of-accounts/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera konto';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function deleteChartOfAccount(id) {
  const res = await fetch(`${API_URL}/chart-of-accounts/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok && res.status !== 204) {
    let msg = 'Kunde inte inaktivera konto';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
}

// --- QA Controls ---

export async function qaListTemplates(params = {}) {
  const includeUnpublished = !!params.includeUnpublished;
  const search = new URLSearchParams();
  if (includeUnpublished) search.append('include_unpublished', 'true');
  const qs = search.toString();
  const url = `${API_URL}/qa/controls/templates${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta mallar');
  return await res.json();
}

export async function qaListMoments(projectId) {
  const res = await fetch(`${API_URL}/qa/controls/projects/${projectId}/moments`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta moment');
  return await res.json();
}

export async function qaCreateMoment(projectId, body) {
  const res = await fetch(`${API_URL}/qa/controls/projects/${projectId}/moments`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa moment';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaUpdateMoment(momentId, body) {
  const res = await fetch(`${API_URL}/qa/controls/moments/${momentId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera moment';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaDeleteMoment(momentId) {
  const res = await fetch(`${API_URL}/qa/controls/moments/${momentId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort moment';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}


export async function qaGetProjectSummary(projectId) {
  const res = await fetch(`${API_URL}/qa/controls/projects/${projectId}/summary`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta sammanfattning');
  return await res.json();
}




// Append tenant_slug query param to file URLs so that cross-origin <img> / PDF
// requests (which cannot carry X-Tenant-Slug headers) still pass tenant context.
function _qaFileUrl(rawUrl) {
  const url = String(rawUrl || '').trim();
  if (!url) return null;
  const full = url.startsWith('/') ? `${API_URL}${url}` : url;
  const slug = getActiveTenantSlug();
  if (!slug) return full;
  const sep = full.includes('?') ? '&' : '?';
  return `${full}${sep}tenant_slug=${encodeURIComponent(slug)}`;
}

export async function qaListProjectControlplanDrawings(projectId) {
  const res = await fetch(`${API_URL}/qa/controls/projects/${projectId}/controlplan-drawings`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta ritningar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  const data = await res.json();
  const drawings = Array.isArray(data?.drawings) ? data.drawings : [];
  return {
    ...(data || {}),
    drawings: drawings.map((d) => {
      const file_url = _qaFileUrl(d?.file_url);
      return { ...(d || {}), file_url: file_url || null };
    }),
  };
}

export async function qaUploadProjectControlplanDrawingToLibrary(projectId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/qa/controls/projects/${projectId}/controlplan-drawings/upload`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: fd,
  });
  if (!res.ok) {
    let msg = 'Kunde inte ladda upp ritning';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  const data = await res.json();
  const drawing = data?.drawing && typeof data.drawing === 'object' ? data.drawing : null;
  const file_url = _qaFileUrl(drawing?.file_url || data?.file_url);
  return {
    ...(data || {}),
    drawing: drawing ? { ...(drawing || {}), file_url: file_url || null } : drawing,
  };
}

export async function qaDeleteProjectControlplanDrawing(projectId, drawingId) {
  const res = await fetch(`${API_URL}/qa/controls/projects/${projectId}/controlplan-drawings/${encodeURIComponent(drawingId)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort ritning';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  const data = await res.json();
  const drawings = Array.isArray(data?.drawings) ? data.drawings : [];
  return {
    ...(data || {}),
    drawings: drawings.map((d) => {
      const file_url = _qaFileUrl(d?.file_url);
      return { ...(d || {}), file_url: file_url || null };
    }),
  };
}


export async function qaListControlpoints(momentId) {
  const res = await fetch(`${API_URL}/qa/controls/moments/${momentId}/controlpoints`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta kontrollpunkter');
  return await res.json();
}

export async function qaListProjectControlpoints(projectId, params = {}) {
  const search = new URLSearchParams();
  if (params.momentId != null && String(params.momentId).trim() !== '') search.append('moment_id', String(params.momentId));
  if (params.status != null && String(params.status).trim() !== '') search.append('status', String(params.status));
  if (params.q != null && String(params.q).trim() !== '') search.append('q', String(params.q));
  const qs = search.toString();
  const url = `${API_URL}/qa/controls/projects/${projectId}/controlpoints${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta kontrollpunkter');
  return await res.json();
}

export async function qaCreateControlpoint(momentId, body) {
  const res = await fetch(`${API_URL}/qa/controls/moments/${momentId}/controlpoints`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa kontrollpunkt';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaUpdateControlpoint(controlpointId, body) {
  const res = await fetch(`${API_URL}/qa/controls/controlpoints/${controlpointId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera kontrollpunkt';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaDeleteControlpoint(controlpointId) {
  const res = await fetch(`${API_URL}/qa/controls/controlpoints/${controlpointId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort kontrollpunkt';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaCreateIssueFromControlpoint(controlpointId, body) {
  const res = await fetch(`${API_URL}/qa/controls/controlpoints/${controlpointId}/create-issue`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa ärende';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaApplyTemplate(projectId, body) {
  const res = await fetch(`${API_URL}/qa/controls/projects/${projectId}/apply-template`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte applicera mall';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- QA Egenkontroller ---

export async function qaEkListTemplates(params = {}) {
  const includeUnpublished = !!params.includeUnpublished;
  const search = new URLSearchParams();
  if (includeUnpublished) search.append('include_unpublished', 'true');
  const qs = search.toString();
  const url = `${API_URL}/qa/egenkontroller/templates${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta egenkontroll-mallar');
  return await res.json();
}

export async function qaEkGetTemplateDetail(templateId) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/templates/${templateId}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta mall-detaljer');
  return await res.json();
}



export async function qaEkListInstances(projectId) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/projects/${projectId}/instances`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta egenkontroller');
  return await res.json();
}

export async function qaEkCreateInstance(projectId, body) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/projects/${projectId}/instances`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa egenkontroll';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkCreateCustomInstance(projectId, body) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/projects/${projectId}/instances/custom`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = `Kunde inte skapa egen egenkontroll (${res.status})`;
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail);
    } catch {
      try {
        const text = await res.text();
        const trimmed = String(text || '').trim();
        if (trimmed) msg = `${msg}: ${trimmed.slice(0, 300)}`;
      } catch {}
    }
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkUpdateInstance(instanceId, body) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/instances/${instanceId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara egenkontroll';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkUploadImage(instanceId, file) {
  const fd = new FormData();
  fd.append('file', file);

  const res = await fetch(`${API_URL}/qa/egenkontroller/instances/${instanceId}/images/upload`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: fd,
  });
  if (!res.ok) {
    let msg = 'Kunde inte ladda upp bild';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkDeleteInstance(instanceId) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/instances/${instanceId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort egenkontroll';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkCreateIssue(instanceId, body) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/instances/${instanceId}/create-issue`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa ärende från egenkontroll';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkImportRegister(params = {}) {
  const search = new URLSearchParams();
  if (params.scope) search.append('scope', String(params.scope));
  if (typeof params.publish === 'boolean') search.append('publish', params.publish ? 'true' : 'false');
  if (typeof params.dryRun === 'boolean') search.append('dry_run', params.dryRun ? 'true' : 'false');
  if (params.path) search.append('path', String(params.path));
  const qs = search.toString();
  const url = `${API_URL}/qa/egenkontroller/templates/import-register${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { method: 'POST', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Kunde inte importera egenkontroll-registret';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkCreateTemplate(body) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/templates`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa mall';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkUpdateTemplate(templateId, body) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/templates/${templateId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera mall';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkDeleteTemplate(templateId) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/templates/${templateId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort mall';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkDuplicateTemplate(templateId) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/templates/${templateId}/duplicate`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte duplicera mall';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkGetInstanceHistory(instanceId) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/instances/${instanceId}/history`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta historik';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export function qaEkExportPdfUrl(projectId) {
  return `${API_URL}/qa/egenkontroller/projects/${projectId}/kontrollplan/pdf`;
}

export function qaControlplanExportCsvUrl(projectId) {
  return `${API_URL}/qa/controls/projects/${projectId}/export/csv`;
}

export async function qaEkBatchImport(projectId, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/qa/egenkontroller/projects/${projectId}/instances/batch-import`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: form,
  });
  if (!res.ok) {
    let msg = 'Kunde inte importera egenkontroller';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkImportCapabilities() {
  const res = await fetch(`${API_URL}/qa/egenkontroller/import/capabilities`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta importbehörigheter';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkParseImportFile(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/qa/egenkontroller/import/parse`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: form,
  });
  if (!res.ok) {
    let msg = 'Kunde inte analysera filen';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkParseImportFiles(files) {
  const form = new FormData();
  (files || []).forEach((file) => form.append('files', file));
  const res = await fetch(`${API_URL}/qa/egenkontroller/import/parse-batch`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: form,
  });
  if (!res.ok) {
    let msg = 'Kunde inte analysera filerna';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkRefineDescription(body) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/import/refine-description`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'KanAI-förfining misslyckades';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkAmaLookup(code) {
  const res = await fetch(`${API_URL}/ek-bibliotek/kanai/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      message: `Förklara vad AMA-koden "${code}" innebär. Ge en kort sammanfattning av krav, toleranser och kontrollmetoder. Svara på svenska.`,
      context: `ama_code_lookup:${code}`,
      history: [],
    }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta AMA-information';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkSaveImportedProject(projectId, body) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/import/save-project/${projectId}`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa importerad egenkontroll';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkSaveImportedTemplate(body) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/import/save-template`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa importerad mall';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function qaEkSaveImportedTemplateBatch(body) {
  const res = await fetch(`${API_URL}/qa/egenkontroller/import/save-template-batch`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte batch-spara importerade mallar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Purchasing (Inköp) ---

export async function purchasingListPurchases(params = {}) {
  const search = new URLSearchParams();
  if (params.project_id) search.append('project_id', String(params.project_id));
  if (params.status) search.append('status', String(params.status));
  if (params.category) search.append('category', String(params.category));
  if (params.supplier_id) search.append('supplier_id', String(params.supplier_id));
  if (params.limit != null) search.append('limit', String(params.limit));
  if (params.offset != null) search.append('offset', String(params.offset));
  const qs = search.toString();
  const url = `${API_URL}/purchasing/purchases${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta inköp');
  return await res.json();
}

export async function purchasingCreatePurchase(body) {
  const res = await fetch(`${API_URL}/purchasing/purchases`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa inköp';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function purchasingUpdatePurchase(purchaseId, body) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera inköp';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function purchasingGetPurchase(purchaseId) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta inköpet');
  return await res.json();
}

// Avrop (pilotspec §2–§3): spårbara avropsposter i stället för direkt statusbyte.
export async function purchasingListCalloffs(purchaseId) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}/calloffs`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta avrop');
  return await res.json();
}

export async function purchasingCreateCalloff(purchaseId, body) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}/calloffs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte registrera avropet';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function purchasingDeleteCalloff(purchaseId, calloffId) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}/calloffs/${calloffId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort avropet';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function purchasingRegisterDelivery(purchaseId, body) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}/delivery`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte registrera leveransen';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function purchasingDeletePurchase(purchaseId) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort inköp';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function purchasingDeleteOffer(purchaseId, offerId) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}/offers/${offerId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort offert';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function purchasingListSuppliers(params = {}) {
  const search = new URLSearchParams();
  if (params.q) search.append('q', String(params.q));
  if (params.ao_scope) search.append('ao_scope', String(params.ao_scope));
  if (params.rating_class) search.append('rating_class', String(params.rating_class));
  if (typeof params.blocked === 'boolean') search.append('blocked', params.blocked ? 'true' : 'false');
  const qs = search.toString();
  const url = `${API_URL}/purchasing/suppliers${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta leverantörer');
  return await res.json();
}

export async function purchasingGetRecentSuppliers() {
  const res = await fetch(`${API_URL}/purchasing/preferences/recent-suppliers`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta senast använda leverantörer');
  const data = await res.json();
  return Array.isArray(data?.supplier_ids) ? data.supplier_ids : [];
}

export async function purchasingSaveRecentSuppliers(supplierIds) {
  const res = await fetch(`${API_URL}/purchasing/preferences/recent-suppliers`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ supplier_ids: (supplierIds || []).map(Number).filter(Number.isFinite) }),
  });
  if (!res.ok) throw new Error('Kunde inte spara senast använda leverantörer');
  return await res.json();
}

export async function purchasingGetOfferWeights() {
  const res = await fetch(`${API_URL}/purchasing/preferences/offer-weights`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta viktprofil');
  const data = await res.json();
  return data?.weights || null;
}

export async function purchasingSaveOfferWeights(weights) {
  const res = await fetch(`${API_URL}/purchasing/preferences/offer-weights`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(weights || {}),
  });
  if (!res.ok) throw new Error('Kunde inte spara viktprofil');
  return await res.json();
}

export async function purchasingSupplierStats() {
  const res = await fetch(`${API_URL}/purchasing/suppliers/stats`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta leverantörsstatistik');
  return await res.json();
}

export async function purchasingCreateSupplier(body) {
  const res = await fetch(`${API_URL}/purchasing/suppliers`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) throw new Error('Kunde inte skapa leverantör');
  return await res.json();
}

export async function purchasingUpdateSupplier(supplierId, body) {
  const res = await fetch(`${API_URL}/purchasing/suppliers/${supplierId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera leverantör';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function purchasingListOffers(purchaseId) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}/offers`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta offerter');
  return await res.json();
}

export async function purchasingCreateOffer(purchaseId, body) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}/offers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa offert';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail);
    } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function purchasingExportOfferComparison(purchaseId, format = 'xlsx') {
  const fmt = String(format || 'xlsx');
  const url = `${API_URL}/purchasing/purchases/${purchaseId}/export?format=${encodeURIComponent(fmt)}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Kunde inte exportera';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail);
    } catch {
      try {
        const text = await res.text();
        if (text) msg = text;
      } catch {}
    }
    throw new Error(msg);
  }

  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || res.headers.get('content-disposition') || '';
  const m = /filename\*?=(?:UTF-8''|"?)([^";]+)"?/i.exec(cd);
  const filename = (m && m[1] ? decodeURIComponent(m[1]) : '') || `offertjamforelse-purchase-${purchaseId}.${fmt}`;
  return { blob, filename };
}

export async function purchasingListOffersScored(purchaseId, weights = {}) {
  const search = new URLSearchParams();
  if (weights.price != null) search.append('w_price', String(weights.price));
  if (weights.transport != null) search.append('w_transport', String(weights.transport));
  if (weights.time != null) search.append('w_time', String(weights.time));
  if (weights.quality != null) search.append('w_quality', String(weights.quality));
  if (weights.env != null) search.append('w_env', String(weights.env));
  const qs = search.toString();
  const url = `${API_URL}/purchasing/purchases/${purchaseId}/offers/scored${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta poäng för offerter');
  return await res.json();
}

export async function purchasingGetPurchaseAudit(purchaseId) {
  const res = await fetch(`${API_URL}/purchasing/purchases/${purchaseId}/audit`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Kunde inte hämta historik');
  return await res.json();
}

export async function purchasingExportOfferComparisonWeighted(purchaseId, format = 'xlsx', weights = {}) {
  const fmt = String(format || 'xlsx');
  const search = new URLSearchParams();
  search.append('format', fmt);
  if (weights.price != null) search.append('w_price', String(weights.price));
  if (weights.transport != null) search.append('w_transport', String(weights.transport));
  if (weights.time != null) search.append('w_time', String(weights.time));
  if (weights.quality != null) search.append('w_quality', String(weights.quality));
  if (weights.env != null) search.append('w_env', String(weights.env));
  const url = `${API_URL}/purchasing/purchases/${purchaseId}/export?${search.toString()}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Kunde inte exportera';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail);
    } catch {
      try {
        const text = await res.text();
        if (text) msg = text;
      } catch {}
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || res.headers.get('content-disposition') || '';
  const m = /filename\*?=(?:UTF-8''|"?)([^";]+)"?/i.exec(cd);
  const filename = (m && m[1] ? decodeURIComponent(m[1]) : '') || `offertjamforelse-purchase-${purchaseId}.${fmt}`;
  return { blob, filename };
}

export async function purchasingGetKpis(params = {}) {
  const search = new URLSearchParams();
  if (params.project_id) search.append('project_id', String(params.project_id));
  if (params.date_from) search.append('date_from', String(params.date_from));
  if (params.date_to) search.append('date_to', String(params.date_to));
  const qs = search.toString();
  const url = `${API_URL}/purchasing/reports/kpis${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta KPI');
  return await res.json();
}

// --- Issue Management (IM) ---

export async function imListIssues(params = {}) {
  const search = new URLSearchParams();
  if (params.project_id) search.append('project_id', String(params.project_id));
  if (params.assignee_user_id) search.append('assignee_user_id', String(params.assignee_user_id));
  if (params.status) search.append('status', params.status);
  if (params.source_module) search.append('source_module', params.source_module);
  if (params.priority) search.append('priority', params.priority);
  if (params.search) search.append('search', params.search);
  if (params.due_from) search.append('due_from', params.due_from);
  if (params.due_to) search.append('due_to', params.due_to);
  if (params.offset != null) search.append('offset', String(params.offset));
  if (params.limit) search.append('limit', String(params.limit));
  const qs = search.toString();
  const url = `${API_URL}/issues/${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta ärenden');
  return await res.json();
}

export async function imListMyIssues(params = {}) {
  const search = new URLSearchParams();
  if (params.project_id) search.append('project_id', String(params.project_id));
  if (params.status) search.append('status', params.status);
  if (params.search) search.append('search', params.search);
  if (params.due_from) search.append('due_from', params.due_from);
  if (params.due_to) search.append('due_to', params.due_to);
  if (params.offset != null) search.append('offset', String(params.offset));
  if (params.limit) search.append('limit', String(params.limit));
  const qs = search.toString();
  const url = `${API_URL}/issues/mine${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta mina ärenden');
  return await res.json();
}

export async function imCreateIssue(body) {
  const res = await fetch(`${API_URL}/issues/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa ärende';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function imUpdateIssue(issueId, body) {
  const res = await fetch(`${API_URL}/issues/${issueId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera ärende';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function imChangeIssueStatus(issueId, body) {
  const res = await fetch(`${API_URL}/issues/${issueId}/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ändra status på ärende';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function imDeleteIssue(issueId) {
  const res = await fetch(`${API_URL}/issues/${issueId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort ärende';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return true;
}

export async function imListIssueComments(issueId) {
  const res = await fetch(`${API_URL}/issues/${issueId}/comments`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta kommentarer för ärende');
  return await res.json();
}

export async function imAddIssueComment(issueId, body) {
  const res = await fetch(`${API_URL}/issues/${issueId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte lägga till kommentar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function imDeleteIssueComment(issueId, commentId) {
  const res = await fetch(`${API_URL}/issues/${issueId}/comments/${commentId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort kommentar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
}

export async function imReorderIssues(items) {
  const res = await fetch(`${API_URL}/issues/reorder`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ändra ordning';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
}

export async function imGetIssueStatusHistory(issueId) {
  const res = await fetch(`${API_URL}/issues/${issueId}/status-history`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta statushistorik');
  return await res.json();
}

export async function imGetIssueChangeHistory(issueId) {
  const res = await fetch(`${API_URL}/issues/${issueId}/change-history`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta ändringshistorik');
  return await res.json();
}

export async function imGetIssuesSummary(params = {}) {
  const search = new URLSearchParams();
  if (params.company_id) search.append('company_id', String(params.company_id));
  const qs = search.toString();
  const url = `${API_URL}/issues/summary${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta ärendeöversikt');
  return await res.json();
}

export async function imListIssueAttachments(issueId) {
  const res = await fetch(`${API_URL}/issues/${issueId}/attachments`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta bilagor för ärende');
  return await res.json();
}

export async function imUploadIssueAttachment(issueId, file, options = {}) {
  const form = new FormData();
  form.append('file', file);
  if (options.folder_id) {
    form.append('folder_id', options.folder_id);
  }
  const res = await fetch(`${API_URL}/issues/${issueId}/attachments`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    let msg = 'Kunde inte ladda upp bilaga';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function imDeleteIssueAttachment(issueId, attachmentId) {
  const res = await fetch(`${API_URL}/issues/${issueId}/attachments/${attachmentId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort bilaga';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  // API returns { ok: true }
  try {
    return await res.json();
  } catch {
    return { ok: true };
  }
}

export async function imListIssueLinks(issueId) {
  const res = await fetch(`${API_URL}/issues/${issueId}/links`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta länkar för ärende');
  return await res.json();
}

export async function imAddIssueLink(issueId, body) {
  const res = await fetch(`${API_URL}/issues/${issueId}/links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte lägga till länk';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Meetings ---

export async function meetingsList(params = {}) {
  const search = new URLSearchParams();
  if (params.project_id) search.append('project_id', String(params.project_id));
  if (params.meeting_type) search.append('meeting_type', params.meeting_type);
  if (params.status) search.append('status', params.status);
  const qs = search.toString();
	const url = `${API_URL}/meetings/${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta möten');
  return await res.json();
}

export async function meetingsListAvailableTemplates(projectId) {
  const pid = Number(projectId);
  if (!Number.isFinite(pid) || pid <= 0) {
    throw new Error('Invalid projectId');
  }

  const res = await fetch(`${API_URL}/projects/${pid}/meetings/templates/available`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta mötesmallar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsListAvailableTemplatesForTenant() {
  const res = await fetch(`${API_URL}/meetings/templates/available`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta mötesmallar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsCreate(body) {
	const res = await fetch(`${API_URL}/meetings/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa möte';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsCreateFollowup(meetingId, body) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}/followup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa uppföljningsmöte';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail);
    } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsDelete(meetingId, { deleteIssues = false, deleteDocuments = false } = {}) {
  const params = new URLSearchParams();
  if (deleteIssues) params.append('delete_issues', 'true');
  if (deleteDocuments) params.append('delete_documents', 'true');
  const qs = params.toString();
  const url = qs ? `${API_URL}/meetings/${meetingId}?${qs}` : `${API_URL}/meetings/${meetingId}`;
  const res = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort möte';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
}

export async function meetingsUpdate(meetingId, body) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera möte';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsUploadAttachments(meetingId, files) {
  const form = new FormData();
  (files || []).forEach((file) => {
    if (file) {
      form.append('files', file);
    }
  });

  const res = await fetch(`${API_URL}/meetings/${meetingId}/attachments`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  if (!res.ok) {
    let msg = 'Kunde inte ladda upp mötesbilaga';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail);
    } catch {}
    throw new Error(msg);
  }

  return await res.json();
}

export async function meetingsUploadAgendaAttachments(itemId, files) {
  const form = new FormData();
  (files || []).forEach((file) => {
    if (file) {
      form.append('files', file);
    }
  });

  const res = await fetch(`${API_URL}/meetings/agenda/${itemId}/attachments`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  if (!res.ok) {
    let msg = 'Kunde inte ladda upp bilaga för agendapunkt';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail);
    } catch {}
    throw new Error(msg);
  }

  return await res.json();
}

export async function meetingsCreateFromTemplate(body) {
  const res = await fetch(`${API_URL}/meetings/from-template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa möte från mall';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsGet(meetingId) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta möte');
  return await res.json();
}

export async function meetingsListParticipants(meetingId) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}/participants`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta deltagare');
  return await res.json();
}

export async function usersSearch(q) {
  const search = new URLSearchParams();
  if (q) search.append('q', q);
  const url = `${API_URL}/users/search?${search.toString()}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Kunde inte söka användare';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsAddParticipant(meetingId, body) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}/participants`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte lägga till deltagare';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsUpdateParticipant(participantId, body) {
  const res = await fetch(`${API_URL}/meetings/participants/${participantId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera deltagare';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsDeleteParticipant(participantId) {
  const res = await fetch(`${API_URL}/meetings/participants/${participantId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort deltagare';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
}

export async function meetingsListAgenda(meetingId) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}/agenda`, { credentials: 'include' });
  if (!res.ok) throw new Error('Kunde inte hämta mötesagenda');
  return await res.json();
}

export async function meetingsAddAgendaItem(meetingId, body) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}/agenda`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte lägga till agendapunkt';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsUpdateAgendaItem(itemId, body) {
  const res = await fetch(`${API_URL}/meetings/agenda/${itemId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera agendapunkt';
    let detail = null;
    try { const data = await res.json(); detail = data?.detail ?? null; if (detail) msg = detail?.meddelande || formatApiErrorDetail(detail); } catch {}
    // status + detail följer med så att Möten känner igen versionskonflikten (ADR 0015).
    const err = new Error(msg); err.status = res.status; err.detail = detail; throw err;
  }
  return await res.json();
}

export async function meetingsDeleteAgendaItem(itemId) {
  const res = await fetch(`${API_URL}/meetings/agenda/${itemId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort agendapunkt';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
}

export async function meetingsCreateIssueFromAgenda(itemId, body) {
  const res = await fetch(`${API_URL}/meetings/agenda/${itemId}/create-issue`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa ärende från agendapunkt';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsExportPdf(meetingId, options = {}) {
  const params = new URLSearchParams();
  if (options && options.sendEmail) params.set('send_email', 'true');
  const qs = params.toString();
  const url = `${API_URL}/meetings/${meetingId}/export-pdf${qs ? `?${qs}` : ''}`;

  const res = await fetch(url, { credentials: 'include' });
  if (res.status === 501) {
    // Stub in backend; surface friendly message
    let msg = 'PDF-export av mötesprotokoll är inte implementerad ännu';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  if (!res.ok) {
    let msg = 'Kunde inte exportera mötesprotokoll';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsGetPdfInfo(meetingId) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}/pdf`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Kunde inte hämta PDF-info för möte';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Meeting signature endpoints ---

export async function meetingsGetSignatureStatus(meetingId) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}/signature-status`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta signaturstatus för möte';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsSignProtocol(meetingId, participantId, signatureImage) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}/participants/${participantId}/sign`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ signature_image: signatureImage }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte signera mötesprotokollet';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsFinalizeForSignature(meetingId) {
  const res = await fetch(`${API_URL}/meetings/${meetingId}/finalize-for-signature`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Kunde inte starta signeringsflöde för möte';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsRefineAgendaText(itemId, field, text) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 60000);
  try {
    const res = await fetch(`${API_URL}/meetings/agenda/${itemId}/refine-text`, {
      method: 'POST',
      headers: addTenantHeader({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({ field, text }),
      signal: controller.signal,
    });
    if (!res.ok) {
      let msg = 'Kunde inte förfina text med AI';
      try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
      throw new Error(msg);
    }
    return await res.json();
  } finally {
    clearTimeout(timeout);
  }
}

export async function meetingsEditAgendaAiText(itemId, field, aiText) {
  const res = await fetch(`${API_URL}/meetings/agenda/${itemId}/ai-text`, {
    method: 'PUT',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ field, ai_text: aiText }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara AI-text';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function meetingsClearAgendaAiText(itemId, field) {
  const res = await fetch(`${API_URL}/meetings/agenda/${itemId}/ai-text`, {
    method: 'DELETE',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ field }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte rensa AI-text';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- KanCalc: quantities, resources, markups, aggregates ---

export async function kcGetRowQuantity(rowId) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/quantity`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    let msg = 'Kunde inte hämta mängd för rad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcUpsertRowQuantity(rowId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/quantity`, {
    method: 'PUT',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera mängd för rad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcListRowResources(rowId) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/resources`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta resurser för rad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcCreateRowResource(rowId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/resources`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa resursrad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcUpdateRowResource(resourceId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/resources/${encodeURIComponent(resourceId)}`, {
    method: 'PATCH',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera resursrad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcDeleteRowResource(resourceId) {
  const res = await fetch(`${API_URL}/api/mapcalc/resources/${encodeURIComponent(resourceId)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort resursrad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
}

export async function kcGetVersionMarkups(versionId) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/markups`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta påslagsinställningar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcUpsertVersionMarkups(versionId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/markups`, {
    method: 'PUT',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara påslagsinställningar';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcGetVersionAggregates(versionId) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/aggregates`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta kalkylsammanställning';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcGetVersionAggregatesByType(versionId) {
  const res = await apiFetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/aggregates/by-type`);
  if (!res.ok) {
    let msg = 'Kunde inte hämta grupperad sammanställning';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcGetVersionAggregatesByRootNode(versionId) {
  const res = await apiFetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/aggregates/by-root-node`);
  if (!res.ok) {
    let msg = 'Kunde inte hämta grupperad sammanställning';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcGetVersionAggregatesByNode(versionId) {
  const res = await apiFetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/aggregates/by-node`);
  if (!res.ok) {
    let msg = 'Kunde inte hämta grupperad sammanställning';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcGetVersionResourceSummary(versionId) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/resources/summary`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta resursöversikt';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcGetVersionResourceOccurrences(versionId, {
  resource_type = null,
  resource_code = null,
  name = null,
  unit = null,
  account = null,
  price_status = null,
} = {}) {
  const usp = new URLSearchParams();
  if (resource_type != null) usp.set('resource_type', String(resource_type));
  if (resource_code != null) usp.set('resource_code', String(resource_code));
  if (name != null) usp.set('name', String(name));
  if (unit != null) usp.set('unit', String(unit));
  if (account != null) usp.set('account', String(account));
  if (price_status != null) usp.set('price_status', String(price_status));

  const qs = usp.toString();
  const res = await apiFetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/resources/occurrences${qs ? `?${qs}` : ''}`);
  if (!res.ok) {
    let msg = 'Kunde inte hämta resursförekomster';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcListVersionMissingUnitResources(versionId) {
  const res = await apiFetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/resources/missing-units`);
  if (!res.ok) {
    let msg = 'Kunde inte hämta resurser utan enhet';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcApplyVersionMissingUnitResources(versionId, body) {
  const res = await apiFetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/resources/missing-units`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara enheter';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcUpdateRow(rowId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}`, {
    method: 'PATCH',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera rad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcDeleteRow(rowId) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort rad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
}

// --- KanCalc: à-pris (per row) ---

export async function kcGetRowAPrice(rowId) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/aprice`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta à-pris för rad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcUpsertRowAPrice(rowId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/aprice`, {
    method: 'PUT',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara à-pris för rad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcGetRow(rowId) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Kunde inte hämta kalkylrad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Erfarenhetsbank (placeholder endpoints) ---
export async function kcExperienceSummary() {
  const res = await fetch(`${API_URL}/api/mapcalc/experience/summary`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta erfarenhetsbank-översikt');
  return await res.json();
}

export async function kcExperienceRow(code) {
  const res = await fetch(`${API_URL}/api/mapcalc/experience/row/${encodeURIComponent(code)}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Kunde inte hämta rad-erfarenhet';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Import / Export ---
export async function kcImportMapCalc(versionId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/import-map`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: fd,
  });
  if (!res.ok) {
    let msg = 'Import misslyckades';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcImportMapCalcSbxml(versionId, netFile, omkFile) {
  const fd = new FormData();
  if (netFile) fd.append('net_file', netFile);
  if (omkFile) fd.append('omk_file', omkFile);
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/import-sbxml`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: fd,
  });
  if (!res.ok) {
    let msg = 'Import (SBXML) misslyckades';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcImportCalcExcel(versionId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/import-excel`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: fd,
  });
  if (!res.ok) {
    let msg = 'Import (Excel) misslyckades';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcExportVersionExcel(versionId) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/export.xlsx`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Export misslyckades';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  return blob;
}

// --- KST: Export Calc → Produktionsbudget ---
export async function kstExportProductionBudget(calcVersionId, lockOfferQuantities = true) {
  const res = await fetch(`${API_URL}/api/kst/export/production-budget`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ calc_version_id: calcVersionId, lock_offer_quantities: lockOfferQuantities })
  });
  if (!res.ok) {
    let msg = 'Export till produktionsbudget misslyckades';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json(); // { budget_version_id, project_id, version_no }
}

// --- Location codes (Lägeskoder) ---
export async function kcListLocationCodes(calcProjectId) {
  const res = await fetch(`${API_URL}/api/mapcalc/projects/${encodeURIComponent(calcProjectId)}/location-codes`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta lägeskoder');
  return await res.json();
}

export async function kcCreateLocationCode(calcProjectId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/projects/${encodeURIComponent(calcProjectId)}/location-codes`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {})
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa lägeskod';
    try {
      const data = await res.json();
      const d = formatApiErrorDetail(data?.detail);
      if (d) msg = d;
    } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcUpdateLocationCode(locationCodeId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/location-codes/${encodeURIComponent(locationCodeId)}`, {
    method: 'PATCH',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {})
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera lägeskod';
    try {
      const data = await res.json();
      const d = formatApiErrorDetail(data?.detail);
      if (d) msg = d;
    } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcDeleteLocationCode(locationCodeId) {
  const res = await fetch(`${API_URL}/api/mapcalc/location-codes/${encodeURIComponent(locationCodeId)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort lägeskod';
    try {
      const data = await res.json();
      const d = formatApiErrorDetail(data?.detail);
      if (d) msg = d;
    } catch {}
    throw new Error(msg);
  }
}

export async function kcGetRowLocationCode(rowId) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/location-code`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    // Allow 404/empty as "no location yet"
    try { const data = await res.json(); if (data?.detail) throw new Error(formatApiErrorDetail(data.detail)); } catch {}
    return null;
  }
  return await res.json();
}

export async function kcSetRowLocationCode(rowId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/location-code`, {
    method: 'PUT',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {})
  });
  if (!res.ok) {
    let msg = 'Kunde inte sätta lägeskod för rad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- UPOSTER per rad ---
export async function kcGetRowUposts(rowId) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/uposts`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Kunde inte hämta uppdelningsposter';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json(); // expected: { items: [{ upost_code, qty }], total_qty }
}

export async function kcSetRowUposts(rowId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/uposts`, {
    method: 'PUT',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {})
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara uppdelningsposter';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Kalkylregister (MAP imports) ---
export async function regList() {
  const res = await fetch(`${API_URL}/api/registers/`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta register');
  return await res.json();
}

export async function regDetail(key) {
  const res = await fetch(`${API_URL}/api/registers/${encodeURIComponent(key)}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta registerdetalj');
  return await res.json();
}

export async function regResources(key, { q = '', limit = 50, offset = 0 } = {}) {
  const usp = new URLSearchParams();
  if (q) usp.set('q', q);
  usp.set('limit', String(limit));
  usp.set('offset', String(offset));
  const res = await fetch(`${API_URL}/api/registers/${encodeURIComponent(key)}/resources?${usp.toString()}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta resurser');
  return await res.json(); // { total, items: [] }
}

export async function regActivities(key, { q = '', limit = 50, offset = 0 } = {}) {
  const usp = new URLSearchParams();
  if (q) usp.set('q', q);
  usp.set('limit', String(limit));
  usp.set('offset', String(offset));
  const res = await fetch(`${API_URL}/api/registers/${encodeURIComponent(key)}/activities?${usp.toString()}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = `Kunde inte hämta aktiviteter (HTTP ${res.status})`;
    try {
      const data = await res.json();
      const detail = formatApiErrorDetail(data?.detail);
      if (detail) msg = `${msg}: ${detail}`;
    } catch {}
    throw new Error(msg);
  }
  return await res.json(); // { total, items: [] }
}

// --- KanCalc: Tenant catalog (egna resurser & recept) ---

export async function kcTenantCatalogMeta() {
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/meta`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Kunde inte hämta tenant-katalog';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcTenantCatalogListResources({ q = '', limit = 50, offset = 0 } = {}) {
  const usp = new URLSearchParams();
  if (q) usp.set('q', q);
  usp.set('limit', String(limit));
  usp.set('offset', String(offset));
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/resources?${usp.toString()}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Kunde inte hämta tenant-resurser';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json(); // { total, items: [] }
}

export async function kcTenantCatalogCreateResource(body) {
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/resources`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa tenant-resurs';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcTenantCatalogUpdateResource(resourceId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/resources/${encodeURIComponent(resourceId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera tenant-resurs';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcTenantCatalogDeleteResource(resourceId) {
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/resources/${encodeURIComponent(resourceId)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort tenant-resurs';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcTenantCatalogImportResources(body) {
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/resources/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte importera resurser till tenant-katalog';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcTenantCatalogListActivities({ q = '', limit = 50, offset = 0 } = {}) {
  const usp = new URLSearchParams();
  if (q) usp.set('q', q);
  usp.set('limit', String(limit));
  usp.set('offset', String(offset));
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/activities?${usp.toString()}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Kunde inte hämta tenant-aktiviteter';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcTenantCatalogGetRecipe(activityCode) {
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/recipes/${encodeURIComponent(activityCode)}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Kunde inte hämta tenant-recept';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcTenantCatalogUpsertRecipe(body) {
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/recipes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara tenant-recept';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcTenantCatalogDeleteRecipe(activityCode) {
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/recipes/${encodeURIComponent(activityCode)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort tenant-recept';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcTenantCatalogImportRecipes(body) {
  const res = await fetch(`${API_URL}/api/mapcalc/tenant-catalog/recipes/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte importera recept till tenant-katalog';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// Apply register recipe to an existing kalkylrad
export async function kcApplyRegisterRecipe(rowId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/apply-register-recipe`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte lägga till recept på rad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// Undo last apply on a kalkylrad
export async function kcUndoLastRowChange(rowId) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/undo-last-change`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ångra senaste ändring';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// Save current row resources as a versioned custom recipe register (project/org)
export async function kcSaveRowAsCustomRecipe(rowId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/rows/${encodeURIComponent(rowId)}/save-as-custom-recipe`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara recept';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function regRecipes(key, { activity_code = '', resource_code = '', limit = 100, offset = 0 } = {}) {
  const usp = new URLSearchParams();
  if (activity_code) usp.set('activity_code', activity_code);
  if (resource_code) usp.set('resource_code', resource_code);
  usp.set('limit', String(limit));
  usp.set('offset', String(offset));
  const res = await fetch(`${API_URL}/api/registers/${encodeURIComponent(key)}/recipes?${usp.toString()}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta recept');
  return await res.json();
}

export async function regPricedRecipes(key, { activity_code = '', limit = 200, offset = 0 } = {}) {
  const usp = new URLSearchParams();
  if (activity_code) usp.set('activity_code', activity_code);
  usp.set('limit', String(limit));
  usp.set('offset', String(offset));
  const res = await fetch(`${API_URL}/api/registers/${encodeURIComponent(key)}/priced-recipes?${usp.toString()}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta prissatta recept');
  return await res.json();
}

// --- Kalkylpaket (Sprint 4) ---

export async function kcListPackages() {
  const res = await fetch(`${API_URL}/api/mapcalc/packages`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta kalkylpaket');
  return await res.json();
}

export async function kcCreatePackage(body) {
  const res = await fetch(`${API_URL}/api/mapcalc/packages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa kalkylpaket';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcCreatePackageItem(packageId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/packages/${encodeURIComponent(packageId)}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa paket-rad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcApplyPackage(versionId, packageId, opts) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/apply-package/${encodeURIComponent(packageId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    credentials: 'include',
    body: JSON.stringify(opts || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte applicera kalkylpaket';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- UNIT4 MAP Import (testmodul) ---
export async function uploadMapMdb(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/api/import/mdbklk`, { method: 'POST', body: form, credentials: 'include' });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to upload MAP file'));
  return await res.json();
}

export async function uploadMapCsv(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/api/import/map/csv`, { method: 'POST', body: form });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to upload MAP CSV'));
  return await res.json();
}

// CSV -> Hierarchy (no persist)
export async function uploadMapCsvHierarchy(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/api/import/map/csv/hierarchy`, { method: 'POST', body: form, credentials: 'include' });
  if (!res.ok) {
    let msg = `Failed to get CSV hierarchy`;
    try { msg = await res.text(); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

// CSV -> Account summary (grouped by parent/child prefixes, no persist)
export async function uploadMapCsvAccountsSummary(file, { parent_len = 2, child_len = 4 } = {}) {
  const form = new FormData();
  form.append('file', file);
  const usp = new URLSearchParams();
  if (parent_len != null) usp.set('parent_len', String(parent_len));
  if (child_len != null) usp.set('child_len', String(child_len));
  const res = await fetch(`${API_URL}/api/import/map/csv/accounts-summary?${usp.toString()}`, { method: 'POST', body: form, credentials: 'include' });
  if (!res.ok) {
    let msg = `Failed to get CSV account summary`;
    try { msg = await res.text(); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json(); // { groups: [...], total }
}

// MDB/MDBKLK -> Hierarchy (no persist)
export async function uploadMapMdbHierarchy(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/api/import/map/mdb/hierarchy`, { method: 'POST', body: form, credentials: 'include' });
  if (!res.ok) {
    let msg = `Failed to get MDB hierarchy`;
    try { msg = await res.text(); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

// MDB/MDBKLK -> Flat enriched export (no persist)
export async function uploadMapMdbFlat(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/api/import/map/mdb/flat`, { method: 'POST', body: form, credentials: 'include' });
  if (!res.ok) {
    let msg = `Failed to get MDB flat export`;
    try { msg = await res.text(); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json(); // { rows, count, warnings }
}

// MDB/MDBKLK -> Profiling info (tables/columns)
export async function uploadMapMdbProfile(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/api/import/map/mdb/profile`, { method: 'POST', body: form, credentials: 'include' });
  if (!res.ok) {
    let msg = `Failed to profile MDB file`;
    try { msg = await res.text(); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json(); // { tables, count, warnings }
}

// Consolidated MAP kalkyl upload (hierarchy + resource summary)
export async function uploadMapCalcMdb(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/api/mapcalc/upload`, { method: 'POST', body: form, credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Failed to process MAP kalkyl';
    try { msg = await res.text(); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json(); // { tree, summary, warnings, count }
}

// --- KanCalc calc core (projects/versions/nodes/rows) ---

export async function kcListProjects() {
  const res = await fetch(`${API_URL}/api/mapcalc/projects`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta kalkylprojekt');
  return await res.json();
}

export async function kcCreateProject(body) {
  const res = await fetch(`${API_URL}/api/mapcalc/projects`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa kalkylprojekt';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcUpdateProject(calcProjectId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/projects/${encodeURIComponent(calcProjectId)}`, {
    method: 'PATCH',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera kalkylprojekt';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcListVersions(calcProjectId) {
  const res = await fetch(`${API_URL}/api/mapcalc/projects/${encodeURIComponent(calcProjectId)}/versions`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta kalkylversioner');
  return await res.json();
}

export async function kcUpdateVersion(versionId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}`, {
    method: 'PATCH',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera kalkylversion';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcGetVersionLinkInfo(versionId) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/link-info`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta version-info';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcCreateVersion(calcProjectId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/projects/${encodeURIComponent(calcProjectId)}/versions`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa kalkylversion';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcListNodes(versionId) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/nodes`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta kalkylnoder');
  return await res.json();
}

export async function kcCreateNode(versionId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/nodes`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa kalkylnod';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcUpdateNode(nodeId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/nodes/${encodeURIComponent(nodeId)}`, {
    method: 'PATCH',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera kalkylnod';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcDeleteNode(nodeId) {
  const res = await fetch(`${API_URL}/api/mapcalc/nodes/${encodeURIComponent(nodeId)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort kalkylnod';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
}

export async function kcListRows(nodeId, opts) {
  const params = new URLSearchParams();
  if (opts?.includeDescendants) params.set('include_descendants', '1');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_URL}/api/mapcalc/nodes/${encodeURIComponent(nodeId)}/rows${qs}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Kunde inte hämta kalkylrader');
  return await res.json();
}

export async function kcListVersionRows(versionId) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/rows`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta kalkylrader';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcCreateRow(nodeId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/nodes/${encodeURIComponent(nodeId)}/rows`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa kalkylrad';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcDiffVersions(fromVersionId, toVersionId, opts) {
  const params = new URLSearchParams();
  if (opts?.tolerance != null) params.set('tolerance', String(opts.tolerance));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(fromVersionId)}/diff/${encodeURIComponent(toVersionId)}${qs}`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte jämföra versioner';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcListVersionChanges(versionId, opts) {
  const params = new URLSearchParams();
  if (opts?.limit != null) params.set('limit', String(opts.limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/changes${qs}`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta ändringslogg';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcBulkUpdateVersionResources(versionId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/resources/bulk-update`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte massuppdatera resurser';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- KanCalc: Inköpspaket (RFQ export/import) ---

export async function kcListProcurementPackages(versionId) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/procurement-packages`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Kunde inte hämta inköpspaket';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcCreateProcurementPackage(versionId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/versions/${encodeURIComponent(versionId)}/procurement-packages`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa inköpspaket';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcAddProcurementPackageItem(packageId, body) {
  const res = await fetch(`${API_URL}/api/mapcalc/procurement-packages/${encodeURIComponent(packageId)}/items`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte lägga till rad i inköpspaket';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function kcExportProcurementPackageRfq(packageId, format = 'xlsx') {
  const res = await fetch(`${API_URL}/api/mapcalc/procurement-packages/${encodeURIComponent(packageId)}/export?format=${encodeURIComponent(format)}`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte exportera RFQ';
    try { msg = await res.text(); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.blob();
}

export async function kcImportProcurementPackageBids(packageId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/api/mapcalc/procurement-packages/${encodeURIComponent(packageId)}/import?format=xlsx`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: fd,
  });
  if (!res.ok) {
    let msg = 'Kunde inte importera anbudspriser';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function listMapImportLogs() {
  const res = await fetch(`${API_URL}/api/import/map/logs`);
  if (!res.ok) throw new Error('Failed to fetch MAP import logs');
  return await res.json();
}

export async function listMapBudgets() {
  const res = await fetch(`${API_URL}/api/import/map/budgets`);
  if (!res.ok) throw new Error('Failed to fetch MAP budgets');
  return await res.json();
}

export async function getMapBudgetTree(externalBudgetId) {
  const res = await fetch(`${API_URL}/api/import/map/budgets/${encodeURIComponent(externalBudgetId)}/tree`);
  if (!res.ok) throw new Error('Failed to fetch MAP budget tree');
  return await res.json();
}

// --- UNIT4 MAP Views & Health ---
export async function getMapFlatRes({ limit = 1000, source = 'samples', external_budget_id } = {}) {
  const params = new URLSearchParams();
  if (limit != null) params.set('limit', String(limit));
  if (source) params.set('source', source);
  if (external_budget_id) params.set('external_budget_id', external_budget_id);
  const res = await fetch(`${API_URL}/api/import/map/flat-res?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch MAP flat resources');
  return await res.json();
}

export async function getMapTotalsByHakt({ source = 'samples', external_budget_id } = {}) {
  const params = new URLSearchParams();
  if (source) params.set('source', source);
  if (external_budget_id) params.set('external_budget_id', external_budget_id);
  const res = await fetch(`${API_URL}/api/import/map/totals/hakt?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch totals by Hakt');
  return await res.json();
}

export async function getMapTotalsByUakt({ source = 'samples', external_budget_id } = {}) {
  const params = new URLSearchParams();
  if (source) params.set('source', source);
  if (external_budget_id) params.set('external_budget_id', external_budget_id);
  const res = await fetch(`${API_URL}/api/import/map/totals/uakt?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch totals by Uakt');
  return await res.json();
}

export async function getMapHealth() {
  const res = await fetch(`${API_URL}/api/import/map/health`);
  if (!res.ok) throw new Error('Failed to fetch MAP health');
  return await res.json();
}

// --- CORS health ---
export async function getCorsHealth() {
  const res = await fetch(`${API_URL}/health/cors`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch CORS health');
  return await res.json(); // { origin, allowed, allow_origins, allow_origin_regex, allow_credentials }
}

// --- Org Chart API ---
export async function fetchOrgChart(params = {}) {
  const usp = new URLSearchParams();
  const type = params.type || 'company';
  usp.set('type', type);
  if (type === 'ao' && params.ao) usp.set('ao', params.ao);
  const res = await fetch(`${API_URL}/orgcharts/${usp.toString() ? `?${usp.toString()}` : ''}`);
  if (!res.ok) throw new Error('Failed to fetch org chart');
  return await res.json();
}

// Org Chart validation
export async function fetchOrgChartValidation(params = {}) {
  const usp = new URLSearchParams();
  const type = params.type || 'company';
  usp.set('type', type);
  if (type === 'ao' && params.ao) usp.set('ao', params.ao);
  const res = await fetch(`${API_URL}/orgcharts/validate${usp.toString() ? `?${usp.toString()}` : ''}`);
  if (!res.ok) throw new Error('Failed to validate org chart');
  return await res.json(); // expected: { issues: [...] } or { items: [...] }
}

// Org Chart PDF export (server-side vector PDF via ReportLab)
export async function exportOrgChartPdf(data) {
  const res = await apiFetch(`${API_URL}/orgcharts/pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Kunde inte generera PDF');
  return await res.blob();
}

export async function orgChartListAoSummary() {
  const res = await fetch(`${API_URL}/orgcharts/ao/summary`);
  if (!res.ok) {
    let msg = 'Kunde inte hämta affärsområden';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function orgChartDeleteAo(value) {
  const target = encodeURIComponent(value);
  const res = await fetch(`${API_URL}/orgcharts/ao/${target}`, { method: 'DELETE' });
  if (!res.ok) {
    let msg = 'Kunde inte rensa affärsområde';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return await res.json();
}

export async function orgChartRenameAo(value, replacement) {
  const target = encodeURIComponent(value);
  const res = await fetch(`${API_URL}/orgcharts/ao/${target}/rename`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ replacement }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte byta namn på affärsområde';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return await res.json();
}

// Update org resource fields from org chart (manager_name, ao)
export async function updateOrgResource(rid, patch) {
  const res = await fetch(`${API_URL}/orgcharts/resources/${rid}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch || {})
  });
  if (!res.ok) {
    let msg = 'Failed to update resource';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return await res.json();
}

export async function createOrgResource(body) {
  const res = await fetch(`${API_URL}/orgcharts/resources`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {})
  });
  if (!res.ok) {
    let msg = 'Failed to create resource';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return await res.json();
}

export async function deleteAllOrgResources() {
  const res = await fetch(`${API_URL}/orgcharts/resources/all`, { method: 'DELETE' });
  if (!res.ok) {
    let msg = 'Failed to delete all resources';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return await res.json();
}

export async function deleteOrgResource(rid) {
  const res = await fetch(`${API_URL}/orgcharts/resources/${rid}`, { method: 'DELETE' });
  if (!res.ok) {
    let msg = 'Failed to delete resource';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return await res.json();
}

// --- User Verification API (Contact → User linkage) ---
export async function getVerificationStatus() {
  const res = await fetch(`${API_URL}/orgcharts/resources/verification-status`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to get verification status');
  return await res.json();
}

export async function getUnverifiedResources() {
  const res = await fetch(`${API_URL}/orgcharts/resources/unverified`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to get unverified resources');
  return await res.json();
}

export async function verifyUsersBatch(resourceIds, autoLink = true) {
  const res = await fetch(`${API_URL}/orgcharts/verify-users`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ resource_ids: resourceIds, auto_link: autoLink }),
  });
  if (!res.ok) {
    let msg = 'Failed to verify users';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function linkResourceToUser(rid, userId) {
  const res = await fetch(`${API_URL}/orgcharts/resources/${rid}/link-user`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ user_id: userId }),
  });
  if (!res.ok) {
    let msg = 'Failed to link user';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function unlinkResourceFromUser(rid) {
  const res = await fetch(`${API_URL}/orgcharts/resources/${rid}/link-user`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Failed to unlink user';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function listTenantUsersForLinking(q = '') {
  const usp = new URLSearchParams();
  if (q) usp.set('q', q);
  const res = await fetch(`${API_URL}/orgcharts/resources/tenant-users${usp.toString() ? `?${usp.toString()}` : ''}`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to list tenant users');
  return await res.json();
}

// --- Assessment / Medarbetarsamtal API (Sprint 1) ---

export async function seedAssessmentCriteria() {
  const res = await fetch(`${API_URL}/api/assessments/criteria/seed-defaults`, { method: 'POST', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to seed criteria');
  return res.json();
}

export async function listAssessmentCriteria({ roundId = null, variant = null } = {}) {
  // round_id locks the result to that round's variant (tjansteman | yrkesarbetare).
  // variant overrides round_id. Without either, the backend returns all variants
  // (legacy behaviour) — pass roundId whenever you have it so the right BARS-set
  // is shown per samtalsrunda.
  const usp = new URLSearchParams();
  if (roundId != null) usp.set('round_id', roundId);
  if (variant) usp.set('variant', variant);
  const qs = usp.toString();
  const res = await fetch(`${API_URL}/api/assessments/criteria${qs ? `?${qs}` : ''}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to list criteria');
  return res.json();
}

export async function getAssessmentCriterion(id) {
  const res = await fetch(`${API_URL}/api/assessments/criteria/${id}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to get criterion');
  return res.json();
}

export async function updateAssessmentCriterion(id, data) {
  const res = await fetch(`${API_URL}/api/assessments/criteria/${id}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update criterion');
  return res.json();
}

export async function createAssessmentRound(data) {
  const res = await fetch(`${API_URL}/api/assessments/rounds`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create round');
  return res.json();
}

export async function listAssessmentRounds(status = '') {
  const usp = new URLSearchParams();
  if (status) usp.set('status', status);
  const res = await fetch(`${API_URL}/api/assessments/rounds${usp.toString() ? `?${usp}` : ''}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to list rounds');
  return res.json();
}

export async function getAssessmentRound(id) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${id}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to get round');
  return res.json();
}

export async function updateAssessmentRound(id, data) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${id}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update round');
  return res.json();
}

export async function deleteAssessmentRound(id) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${id}`, { method: 'DELETE', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to delete round');
  return res.json();
}

export async function deleteAssessmentSession(sessionId) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}`, { method: 'DELETE', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to delete session'); }
  return res.json();
}

export async function clearAssessmentRoundSessions(roundId) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/sessions`, { method: 'DELETE', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to clear sessions'); }
  return res.json();
}

export async function createAssessmentSession(roundId, data) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/sessions`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to create session');
  return res.json();
}

export async function createAssessmentSessionsBatch(roundId, resourceIds) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/sessions/batch`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify({ resource_ids: resourceIds }),
  });
  if (!res.ok) throw new Error('Failed to create sessions batch');
  return res.json();
}

export async function listAssessmentSessions(roundId = null) {
  const usp = new URLSearchParams();
  if (roundId) usp.set('round_id', roundId);
  const res = await fetch(`${API_URL}/api/assessments/sessions${usp.toString() ? `?${usp}` : ''}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to list sessions');
  return res.json();
}

/**
 * Fetch ALL sessions as admin (no X-Simulated-Role header).
 * Used by systemadmin person-picker to browse sessions across all users.
 */
export async function listAllAssessmentSessions() {
  const tenantSlug = getActiveTenantSlug();
  const h = tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {};
  const res = await fetch(`${API_URL}/api/assessments/sessions`, { credentials: 'include', headers: h });
  if (!res.ok) throw new Error('Failed to list all sessions');
  return res.json();
}

/* ---- password-reset (public, no auth) ---- */
export async function forgotPassword(email) {
  const res = await fetch(`${API_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || 'Failed to send password-reset email');
  }
  return res.json();
}

export async function resetPassword(token, newPassword) {
  const res = await fetch(`${API_URL}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || 'Failed to reset password');
  }
  return res.json();
}

/* ---- batch create users from org resources (admin) ---- */
export async function batchCreateUsersFromResources(resourceIds) {
  const body = resourceIds ? { resource_ids: resourceIds } : {};
  const res = await fetch(`${API_URL}/api/users/batch-create-from-resources`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || 'Failed to batch-create users');
  }
  return res.json();
}

export async function getAssessmentSession(id) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${id}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to get session');
  return res.json();
}

export async function updateAssessmentSession(id, data) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${id}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update session');
  return res.json();
}

export async function updateAssessmentRating(ratingId, data) {
  const res = await fetch(`${API_URL}/api/assessments/ratings/${ratingId}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update rating');
  return res.json();
}

export async function updateAssessmentRatingsBatch(sessionId, ratings) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/ratings/batch`, {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify({ ratings }),
  });
  if (!res.ok) throw new Error('Failed to batch-update ratings');
  return res.json();
}

export async function getAssessmentGapAnalysis(sessionId) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/gap-analysis`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to get gap analysis');
  return res.json();
}

export async function getAssessmentHistory(resourceId) {
  const res = await fetch(`${API_URL}/api/assessments/resources/${resourceId}/assessment-history`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to get assessment history');
  return res.json();
}

export async function getAssessmentAICoaching(sessionId) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/ai-coaching`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...addTenantHeader() } });
  if (!res.ok) throw new Error('Failed to get AI coaching');
  return res.json();
}

// --- Assessment Goals (Sprint 6) ---
export async function listAssessmentGoals(sessionId) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/goals`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to list goals');
  return res.json();
}
export async function createAssessmentGoal(sessionId, body) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/goals`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...addTenantHeader() }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error('Failed to create goal');
  return res.json();
}
export async function updateAssessmentGoal(goalId, body) {
  const res = await fetch(`${API_URL}/api/assessments/goals/${goalId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json', ...addTenantHeader() }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error('Failed to update goal');
  return res.json();
}
export async function deleteAssessmentGoal(goalId) {
  const res = await fetch(`${API_URL}/api/assessments/goals/${goalId}`, { method: 'DELETE', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to delete goal');
}
export async function exportAssessmentPDF(sessionId) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/export-pdf`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to export PDF');
  return res.blob();
}

// --- Assessment share-link (admin creates) ---
export async function createAssessmentShareLink(sessionId, role = 'employee', expiresDays = 180) {
  const usp = new URLSearchParams({ role, expires_days: String(expiresDays) });
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/share-link?${usp}`, {
    method: 'POST', credentials: 'include', headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to create share link');
  return res.json();
}

// --- Assessment public/external fill (no cookie auth) ---
export async function getExternalAssessmentSession(token) {
  const res = await fetch(`${API_URL}/api/public/assessments/fill/${token}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Ogiltig eller utgången länk');
  }
  return res.json();
}

export async function updateExternalAssessmentRatings(token, ratings) {
  const res = await fetch(`${API_URL}/api/public/assessments/fill/${token}/ratings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ratings }),
  });
  if (!res.ok) throw new Error('Failed to save ratings');
  return res.json();
}

export async function submitExternalAssessment(token) {
  const res = await fetch(`${API_URL}/api/public/assessments/fill/${token}/submit`, {
    method: 'POST',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Kunde inte skicka in');
  }
  return res.json();
}

// --- Assessment external: save with extra fields (manager_comment, meeting_notes) ---
export async function updateExternalAssessmentRatingsFull(token, { ratings, meeting_notes, manager_overall_comment }) {
  const res = await fetch(`${API_URL}/api/public/assessments/fill/${token}/ratings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ratings, meeting_notes, manager_overall_comment }),
  });
  if (!res.ok) throw new Error('Failed to save ratings');
  return res.json();
}

// --- Assessment samtalsvy (conversation view) ---
export async function getExternalSamtal(token) {
  const res = await fetch(`${API_URL}/api/public/assessments/fill/${token}/samtal`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Kunde inte ladda samtalsdata');
  }
  return res.json();
}

export async function saveExternalSamtal(token, { sections, actionItems }) {
  const res = await fetch(`${API_URL}/api/public/assessments/fill/${token}/samtal`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sections, actionItems }),
  });
  if (!res.ok) throw new Error('Failed to save samtal');
  return res.json();
}

export async function completeExternalSamtal(token) {
  const res = await fetch(`${API_URL}/api/public/assessments/fill/${token}/samtal/complete`, {
    method: 'POST',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Kunde inte markera som genomfört');
  }
  return res.json();
}

// --- Assessment PDF downloads (token-based) ---
export function getExternalSamtalsmallPdfUrl(token) {
  return `${API_URL}/api/public/assessments/fill/${token}/samtalsmall-pdf`;
}
export function getExternalPrepPdfUrl(token) {
  return `${API_URL}/api/public/assessments/fill/${token}/prep-pdf`;
}

// --- Assessment public AI coaching (token-based) ---
export async function getExternalAICoaching(token) {
  const res = await fetch(`${API_URL}/api/public/assessments/fill/${token}/ai-coaching`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Kunde inte ladda AI-rekommendationer');
  }
  return res.json();
}

// --- Assessment protocol (authenticated, internal) ---
export async function getProtocol(sessionId) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/protocol`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Kunde inte ladda protokollet');
  }
  return res.json();
}

export async function saveProtocol(sessionId, { sections, actionItems, criterionNotes }) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/protocol`, {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...addTenantHeader() },
    body: JSON.stringify({ sections, actionItems, criterionNotes }),
  });
  if (!res.ok) throw new Error('Failed to save protocol');
  return res.json();
}

function parseDownloadFilenameFromResponse(res) {
  const cd = res.headers.get('Content-Disposition') || res.headers.get('content-disposition') || '';
  if (!cd) return '';

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]).trim();
    } catch {
      return utf8Match[1].trim();
    }
  }

  const plainMatch = /filename="?([^";]+)"?/i.exec(cd);
  return plainMatch?.[1]?.trim() || '';
}

function sanitizeAssessmentFilenamePart(value) {
  return String(value || '')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isGenericProtocolFilename(filename) {
  return /^protokoll-\d+\.pdf$/i.test(String(filename || '').trim());
}

export function buildAssessmentProtocolFilename({ employeeName, managerName, completedAt } = {}) {
  const employee = sanitizeAssessmentFilenamePart(employeeName) || 'Medarbetare';
  const manager = sanitizeAssessmentFilenamePart(managerName);

  let datePart = '';
  if (completedAt) {
    const parsed = new Date(completedAt);
    if (!Number.isNaN(parsed.getTime())) {
      datePart = parsed.toISOString().slice(0, 10);
    } else {
      datePart = String(completedAt).slice(0, 10);
    }
  }

  const parts = ['Medarbetarsamtal', employee];
  if (manager) parts.push(manager);
  if (datePart) parts.push(datePart);
  return `${parts.join(' - ')}.pdf`;
}

function resolveAssessmentProtocolFilename(res, fallbackFilename, sessionId) {
  const serverFilename = parseDownloadFilenameFromResponse(res);
  if (serverFilename && !isGenericProtocolFilename(serverFilename)) return serverFilename;
  if (fallbackFilename) return fallbackFilename;
  return `protokoll-${sessionId}.pdf`;
}

export function getProtocolPdfUrl(sessionId) {
  return `${API_URL}/api/assessments/sessions/${sessionId}/protocol-pdf`;
}
export async function downloadProtocolPdf(sessionId, fallbackFilename) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/protocol-pdf`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to download protocol PDF');
  const blob = await res.blob();
  return { blob, filename: resolveAssessmentProtocolFilename(res, fallbackFilename, sessionId) };
}
export async function downloadPrepPdf(sessionId) {
  const res = await fetch(`${API_URL}/api/assessments/sessions/${sessionId}/prep-pdf`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to download prep PDF');
  return res.blob();
}

// --- Assessment batch link generation ---
export async function generateAssessmentBatchLinks(roundId, { regenerate = false, session_ids } = {}) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/generate-links`, {
    method: 'POST', credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ regenerate, session_ids }),
  });
  if (!res.ok) throw new Error('Failed to generate links');
  return res.json();
}

// --- Assessment extend & resend (förläng runda + skicka nya länkar till pending) ---
export async function extendAndResendAssessment(roundId, { extend_days = 30, dry_run = false, test_email = null } = {}) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/extend-and-resend`, {
    method: 'POST', credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ extend_days, dry_run, test_email }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte förlänga runda';
    try { const j = await res.json(); msg = j.detail || msg; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

// --- Assessment per-session extend (force ny medarbetarlänk) ---
export async function extendEmployeeSession(roundId, sessionId, { extend_days = 30, reopen_self_assessment = false } = {}) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/sessions/${sessionId}/extend-employee`, {
    method: 'POST', credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ extend_days, reopen_self_assessment }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte förlänga länken';
    try { const j = await res.json(); msg = j.detail || msg; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

// --- Assessment refresh managers from org ---
export async function refreshAssessmentManagers(roundId) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/refresh-managers`, {
    method: 'POST', credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('Failed to refresh managers');
  return res.json();
}

export async function batchUpdateAssessmentManagers(roundId, updates) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/batch-update-managers`, {
    method: 'POST', credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates }),
  });
  if (!res.ok) throw new Error('Failed to batch update managers');
  return res.json();
}

// --- Assessment manual session creation ---
export async function createManualAssessmentSession(roundId, { employee_name, employee_email, manager_name, manager_email }) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/manual-session`, {
    method: 'POST', credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ employee_name, employee_email, manager_name, manager_email }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(formatApiErrorDetail(data.detail) || 'Failed to create manual session');
  }
  return res.json();
}

// --- Assessment batch email send ---
export async function sendAssessmentBatchEmails(roundId, { session_ids, roles = ['employee', 'manager'] }) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/send-emails`, {
    method: 'POST', credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_ids, roles }),
  });
  if (!res.ok) throw new Error('Failed to send emails');
  return res.json();
}

// --- Assessment round dashboard (admin) ---
export async function getAssessmentDashboard(roundId) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/dashboard`, {
    credentials: 'include', headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to load dashboard');
  return res.json();
}

// --- Assessment round report (admin) ---
export async function getAssessmentReport(roundId) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/report`, {
    credentials: 'include', headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to load report');
  return res.json();
}

// --- Manager dashboard (public, token-based) ---
export async function getManagerDashboardPublic(token) {
  const res = await fetch(`${API_URL}/api/public/assessments/manager-dashboard/${token}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Ogiltig eller utgången länk');
  }
  return res.json();
}

export async function sendManagerReminder(dashboardToken, sessionId) {
  const res = await fetch(`${API_URL}/api/public/assessments/manager-dashboard/${dashboardToken}/remind/${sessionId}`, {
    method: 'POST',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Kunde inte skicka påminnelse');
  }
  return res.json();
}

export async function getManagerProtocol(dashboardToken, sessionId) {
  const res = await fetch(`${API_URL}/api/public/assessments/manager-dashboard/${dashboardToken}/protocol/${sessionId}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Kunde inte hämta protokoll');
  }
  return res.json();
}

export async function downloadManagerProtocolPdf(dashboardToken, sessionId, fallbackFilename) {
  const res = await fetch(`${API_URL}/api/public/assessments/manager-dashboard/${dashboardToken}/protocol/${sessionId}/pdf`);
  if (!res.ok) throw new Error('Kunde inte ladda ner PDF');
  const blob = await res.blob();
  return { blob, filename: resolveAssessmentProtocolFilename(res, fallbackFilename, sessionId) };
}

// --- Manager dashboard links (admin, authenticated) ---
export async function generateManagerDashboardLinks(roundId) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/manager-dashboard-links`, {
    method: 'POST', credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('Failed to generate manager dashboard links');
  return res.json();
}

export async function sendManagerDashboardEmails(roundId, { manager_resource_ids } = {}) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/send-manager-dashboard-emails`, {
    method: 'POST', credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ manager_resource_ids }),
  });
  if (!res.ok) throw new Error('Failed to send manager dashboard emails');
  return res.json();
}

// --- Regenerate all tokens in a round (admin, no emails sent) ---
export async function regenerateRoundTokens(roundId, { expires_days = 180 } = {}) {
  const res = await fetch(`${API_URL}/api/assessments/rounds/${roundId}/regenerate-tokens`, {
    method: 'POST', credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ expires_days }),
  });
  if (!res.ok) {
    let detail = 'Kunde inte förnya länkar';
    try { const j = await res.json(); detail = j.detail || detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json();
}

// --- Resource Planning API ---
export async function rpListResources(params = {}) {
  const usp = new URLSearchParams();
  if (params.q) usp.set('q', params.q);
  if (params.ao) usp.set('ao', params.ao);
  if (params.role) usp.set('role', params.role);
  if (params.active != null) usp.set('active', String(params.active));
  const res = await fetch(`${API_URL}/resource-planning/resources${usp.toString() ? `?${usp.toString()}` : ''}`);
  if (!res.ok) throw new Error('Failed to list resources');
  return await res.json();
}

export async function rpListAllocations(params = {}) {
  const usp = new URLSearchParams();
  if (params.resource_id) usp.set('resource_id', params.resource_id);
  if (params.project_id) usp.set('project_id', params.project_id);
  if (params.from_date) usp.set('from_date', params.from_date);
  if (params.to_date) usp.set('to_date', params.to_date);
  if (params.expand) usp.set('expand', params.expand);
  const res = await fetch(`${API_URL}/resource-planning/allocations${usp.toString() ? `?${usp.toString()}` : ''}`);
  if (!res.ok) throw new Error('Failed to list allocations');
  return await res.json();
}

// --- Competences API ---
export async function rpListCompetences(params = {}) {
  const usp = new URLSearchParams();
  if (params.q) usp.set('q', params.q);
  if (params.category) usp.set('category', params.category);
  const res = await fetch(`${API_URL}/resource-planning/competences${usp.toString() ? `?${usp.toString()}` : ''}`);
  if (!res.ok) throw new Error('Failed to list competences');
  return await res.json();
}

export async function rpListResourceCompetences(params = {}) {
  const usp = new URLSearchParams();
  if (params.resource_id) usp.set('resource_id', params.resource_id);
  if (params.competence_id) usp.set('competence_id', params.competence_id);
  const res = await fetch(`${API_URL}/resource-planning/resource-competences${usp.toString() ? `?${usp.toString()}` : ''}`);
  if (!res.ok) throw new Error('Failed to list resource competences');
  return await res.json();
}

export async function rpCreateResourceCompetence(body) {
  const res = await fetch(`${API_URL}/resource-planning/resource-competences`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error('Failed to assign competence');
  return await res.json();
}

export async function rpDeleteResourceCompetence(id) {
  const res = await fetch(`${API_URL}/resource-planning/resource-competences/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to remove competence');
  return true;
}

export async function rpImportAbsenceCsv(file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/resource-planning/absence/import-csv`, { method: 'POST', body: fd });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Import failed'); }
  return res.json();
}

export async function rpProjectNeeds(projectId) {
  const res = await fetch(`${API_URL}/resource-planning/projects/${projectId}/needs`);
  if (!res.ok) throw new Error('Failed to fetch project needs');
  return res.json();
}

export async function rpRbacContext() {
  const res = await fetch(`${API_URL}/resource-planning/rbac-context`);
  if (!res.ok) return { is_admin: false, can_write: false, ao_scope: null };
  return res.json();
}

export async function rpAiSuggest(body) {
  const res = await fetch(`${API_URL}/resource-planning/ai-suggest`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...addTenantHeader() }, body: JSON.stringify(body), credentials: 'include'
  });
  if (!res.ok) throw new Error('AI-förslag misslyckades');
  return await res.json();
}

export async function rpOutlookAvailability(resourceId, fromDate, toDate) {
  const usp = new URLSearchParams({ resource_id: resourceId, from_date: fromDate, to_date: toDate });
  const res = await fetch(`${API_URL}/resource-planning/outlook-availability?${usp}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) return { busy_blocks: [], status: 'error' };
  return await res.json();
}

export async function rpCreateAllocation(body) {
  const res = await fetch(`${API_URL}/resource-planning/allocations`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
  });
  if (!res.ok) {
    let detail = 'Failed to create allocation';
    try { const data = await res.json(); if (data?.detail) detail = data.detail; } catch {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  try {
    const h = res.headers.get('X-Overlap-Conflict');
    if (h === '1') data.__overlap = true;
  } catch {}
  return data;
}

export async function rpUpdateAllocation(id, body) {
  const res = await fetch(`${API_URL}/resource-planning/allocations/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error('Failed to update allocation');
  return await res.json();
}

export async function rpDeleteAllocation(id) {
  const res = await fetch(`${API_URL}/resource-planning/allocations/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete allocation');
  return await res.json();
}

export async function rpListAbsence(params = {}) {
  const usp = new URLSearchParams();
  if (params.resource_id) usp.set('resource_id', params.resource_id);
  if (params.from_date) usp.set('from_date', params.from_date);
  if (params.to_date) usp.set('to_date', params.to_date);
  const res = await fetch(`${API_URL}/resource-planning/absence${usp.toString() ? `?${usp.toString()}` : ''}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to list absence');
  return await res.json();
}

export async function rpCreateAbsence(body) {
  const res = await fetch(`${API_URL}/resource-planning/absence`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), credentials: 'include'
  });
  if (!res.ok) {
    let detail = 'Failed to create absence';
    try { const data = await res.json(); if (data?.detail) detail = data.detail; } catch {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return await res.json();
}

export async function rpDeleteAbsence(id) {
  const res = await fetch(`${API_URL}/resource-planning/absence/${id}`, { method: 'DELETE', credentials: 'include' });
  if (!res.ok) throw new Error('Failed to delete absence');
  return await res.json();
}

export async function adminSeedResourcePlanningDemo() {
  // apiFetch, inte fetch: anropet maste bara med organisations-headern.
  // Endpointen skapar personalposter, och sedan personalregistret ligger
  // bakom RLS (20260811f) avvisas en post utan organisation av databasen.
  const res = await apiFetch(`${API_URL}/admin/seed-resource-planning-demo`, { method: 'POST' });
  if (!res.ok) throw new Error('Failed to seed resource planning demo');
  return await res.json();
}

// --- Revisions API ---
export function getActiveTenantSlug() {
  let tenantSlug = '';
  try { tenantSlug = String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim(); } catch {}
  return tenantSlug;
}

function addTenantHeader(headers = {}) {
  const tenantSlug = getActiveTenantSlug();
  const h = tenantSlug ? { ...(headers || {}), 'X-Tenant-Slug': tenantSlug } : { ...(headers || {}) };
  // Role / user simulation headers (systemadmin-only, stored via Layout toggle)
  try {
    const simRole = (localStorage.getItem('kanplan-simulated-role') || '').trim();
    if (simRole) h['X-Simulated-Role'] = simRole;
    const simUser = (localStorage.getItem('kanplan-simulated-user') || '').trim();
    if (simUser) h['X-Simulated-User'] = simUser;
  } catch { /* ignore */ }
  // Inskrivning-admin-token (extern PL-atkomst via JWT-lank). Session-scoped sa
  // token bara overlever fliken — minskar lacka-risk om enheten lamnas oppen.
  try {
    const inskAdminToken = (sessionStorage.getItem('kanplan-inskrivning-admin-token') || '').trim();
    if (inskAdminToken) h['X-Inskrivning-Admin-Token'] = inskAdminToken;
  } catch { /* ignore */ }
  return h;
}

// Shared helper for modules that call API directly.
// Always includes cookies and the current tenant header when available.
export async function apiFetch(url, options = {}) {
  // Prefix API_URL om URL ar relativ (/api/..., /tenants/..., etc).
  // Pa stage/prod ligger frontend och backend pa olika subdomaner — relativa
  // URL:er resolveras mot frontend-origin och ger HTML 404 istallet for JSON.
  // Kallare som redan har absolut URL (https://...) eller redan har API_URL
  // som prefix lamnas oroda.
  const isAbsolute = /^https?:\/\//i.test(url);
  const alreadyPrefixed = !isAbsolute && API_URL && url.startsWith(API_URL);
  const resolvedUrl = (isAbsolute || alreadyPrefixed || !url.startsWith('/'))
    ? url
    : `${API_URL}${url}`;
  const headers = addTenantHeader(options.headers || {});
  return fetch(resolvedUrl, {
    ...options,
    credentials: 'include',
    headers,
  });
}

// --- Training (Onboarding V2) ---

export async function trainingGetOnboardingRole() {
  const res = await apiFetch(`${API_URL}/training/v2/onboarding/role`);
  if (!res.ok) {
    let msg = 'Kunde inte hämta onboarding-roll';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json(); // { role, source }
}

export async function trainingSetOnboardingRole(role_code) {
  const res = await apiFetch(`${API_URL}/training/v2/onboarding/role`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role_code: String(role_code || '').trim() }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara onboarding-roll';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json(); // { ok, role }
}

export async function trainingAddOnboardingOptIn(module_id) {
  const res = await apiFetch(`${API_URL}/training/v2/onboarding/optins`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ module_id: String(module_id || '').trim() }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte lägga till valbar modul';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json(); // { ok }
}

export async function trainingRemoveOnboardingOptIn(module_id) {
  const usp = new URLSearchParams();
  usp.set('module_id', String(module_id || '').trim());
  const res = await apiFetch(`${API_URL}/training/v2/onboarding/optins?${usp.toString()}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort valbar modul';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json(); // { ok }
}

export async function trainingGetCertificationDashboardMe() {
  const res = await apiFetch(`${API_URL}/training/v2/certification-dashboard/me`);
  if (!res.ok) {
    let msg = 'Kunde inte hämta certifieringsdashboard';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function trainingGetMyOnboardingPlan() {
  const res = await apiFetch(`${API_URL}/training/v2/onboarding/my-plan`);
  if (!res.ok) {
    let msg = 'Kunde inte hämta onboarding-plan';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function listRevisions(params = {}) {
  const usp = new URLSearchParams();
  if (params.project_id != null) usp.set('project_id', String(params.project_id));
  if (params.status) usp.set('status', params.status);
  if (params.type) usp.set('type', params.type);
  if (params.from_date) usp.set('from_date', params.from_date);
  if (params.to_date) usp.set('to_date', params.to_date);
  const qs = usp.toString();
  const url = qs ? `${API_URL}/revisions?${qs}` : `${API_URL}/revisions`;
  const res = await fetch(url, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let detail = 'Failed to list revisions';
    try {
      const data = await res.json();
      if (data?.detail) detail = data.detail;
    } catch {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return await res.json(); // { items: [...] }
}

export async function createRevision(body) {
  const res = await fetch(`${API_URL}/revisions`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) {
    let detail = 'Failed to create revision';
    try {
      const data = await res.json();
      if (data?.detail) detail = data.detail;
    } catch {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return await res.json();
}

export async function listRevisionTemplates() {
  const res = await fetch(`${API_URL}/revisions/templates`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to list revision templates');
  return await res.json(); // { items: [...] }
}

export async function adminSeedRevisionTemplates() {
  const res = await fetch(`${API_URL}/admin/seed-revision-templates`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!res.ok) throw new Error('Failed to seed revision templates');
  return await res.json();
}

export async function adminRevisionTemplatesHealth() {
  const res = await fetch(`${API_URL}/admin/revision-templates/health`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch templates health');
  return await res.json();
}

export async function deleteRevision(id) {
  const res = await fetch(`${API_URL}/revisions/${id}`, { method: 'DELETE', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok && res.status !== 204) {
    let detail = 'Failed to delete revision';
    try { const j = await res.json(); detail = j.detail || detail; } catch {}
    throw new Error(detail);
  }
}

export async function getRevision(id) {
  const res = await fetch(`${API_URL}/revisions/${id}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    // Statuskoden foljer med sa att vyn kan skilja "nekad" (403) fran
    // "finns inte" (404) och tekniskt fel.
    const err = new Error('Failed to get revision');
    err.status = res.status;
    throw err;
  }
  return await res.json();
}

export async function addRevisionPoints(revisionId, items) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/points`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(items || []),
  });
  if (!res.ok) throw new Error('Failed to add revision points');
  return await res.json();
}

export async function updateRevisionPoint(pointId, patch) {
  const res = await fetch(`${API_URL}/revisions/revision-points/${pointId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(patch || {}),
  });
  if (!res.ok) throw new Error('Failed to update revision point');
  return await res.json();
}

export async function deleteRevisionPoint(pointId) {
  const res = await fetch(`${API_URL}/revisions/revision-points/${pointId}`, { method: 'DELETE', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok && res.status !== 204) throw new Error('Failed to delete revision point');
}

export async function addRevisionFinding(revisionId, payload) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/findings`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) throw new Error('Failed to add finding');
  return await res.json();
}

export async function updateRevisionFinding(findingId, patch) {
  const res = await fetch(`${API_URL}/revisions/revision-findings/${findingId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(patch || {}),
  });
  if (!res.ok) throw new Error('Failed to update finding');
  return await res.json();
}

export async function deleteRevisionFinding(findingId) {
  const res = await fetch(`${API_URL}/revisions/revision-findings/${findingId}`, { method: 'DELETE', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok && res.status !== 204) throw new Error('Failed to delete finding');
}

export async function copyFindingToIm(revisionId, findingId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/findings/${findingId}/copy-to-im`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to copy finding to IM');
  return await res.json();
}

export async function batchCopyFindingsToIm(revisionId, findingIds) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/findings/batch-copy-to-im`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ finding_ids: findingIds }),
  });
  if (!res.ok) throw new Error('Failed to batch copy findings to IM');
  return await res.json();
}

export async function classifyFinding(findingId, persistConfidence = true) {
  const res = await fetch(`${API_URL}/revisions/revision-findings/${findingId}/classify${persistConfidence ? '' : '?persist_confidence=false'}`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to classify finding');
  return await res.json(); // { finding_id, finding_type, severity, confidence, rationale }
}

// level: 'kort' | 'normal' | 'utforlig' — styr hur ingaende forfiningen blir.
// Okanda varden faller tillbaka till 'normal' i backend.
export async function refineRevisionComment(pointId, commentId, text, level) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 60_000);
  try {
    const res = await fetch(`${API_URL}/revisions/revision-points/${pointId}/refine-comment`, {
      method: 'POST',
      credentials: 'include',
      headers: addTenantHeader({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ comment_id: commentId, text, level }),
      signal: controller.signal,
    });
    if (!res.ok) {
      let msg = 'AI-förfining misslyckades';
      try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
      throw new Error(msg);
    }
    return await res.json(); // { comment_id, ai_text }
  } finally {
    clearTimeout(timer);
  }
}

// En omgang ar tidsbudgeterad i backend (~2 min) och returnerar hur manga
// kommentarer som ar kvar. Klient-timeouten ligger under lastbalanserarens
// 300s sa ett hangande anrop ger ett begripligt fel i stallet for "Failed to fetch".
export async function bulkRefineRevisionComments(revisionId, level) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 240_000);
  try {
    const res = await fetch(`${API_URL}/revisions/${revisionId}/bulk-refine-comments`, {
      method: 'POST',
      credentials: 'include',
      headers: addTenantHeader({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ level }),
      signal: controller.signal,
    });
    if (!res.ok) {
      let msg = 'Bulk AI-förfining misslyckades';
      try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
      throw new Error(msg);
    }
    return await res.json(); // { total_refined, skipped, errors, remaining }
  } finally {
    clearTimeout(timer);
  }
}

// Tar bort AI-forfiningar. Utan commentId rensas punktens hela trad.
// Originaltexten lamnas alltid orord.
export async function clearRevisionPointRefinements(pointId, commentId) {
  const res = await fetch(`${API_URL}/revisions/revision-points/${pointId}/clear-refinements`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ comment_id: commentId ?? null }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte rensa AI-förfiningen';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json(); // { point_id, cleared }
}

export async function clearRevisionRefinements(revisionId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/clear-refinements`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte rensa AI-förfiningarna';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json(); // { revision_id, cleared, points_affected }
}

export async function patchRevision(id, patch) {
  const res = await fetch(`${API_URL}/revisions/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(patch || {}),
  });
  if (!res.ok) {
    let detail = 'Failed to update revision';
    try { const body = await res.json(); if (body.detail) detail = body.detail; } catch {}
    const err = new Error(detail);
    err.detail = detail;
    throw err;
  }
  return await res.json();
}

export async function listRevisionFiles(revisionId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/files`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to list revision files');
  return await res.json();
}

export async function uploadRevisionFile(revisionId, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/revisions/${revisionId}/files`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: form,
  });
  if (!res.ok) throw new Error('Failed to upload file');
  return await res.json();
}

export async function deleteRevisionFile(revisionId, fileId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/files/${fileId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to delete file');
}

export async function generateRevisionReport(revisionId) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 120_000);
  try {
    const res = await fetch(`${API_URL}/revisions/${revisionId}/generate-report`, { method: 'POST', credentials: 'include', headers: addTenantHeader(), signal: controller.signal });
    if (!res.ok) throw new Error('Failed to generate report');
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

export async function generateMultiRevisionSummary(revisionIds) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 180_000);
  try {
    const res = await fetch(`${API_URL}/revisions/multi-summary`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ revision_ids: revisionIds }),
      signal: controller.signal,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(detail || 'Failed to generate multi-revision summary');
    }
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

export async function exportMultiSummaryPdf({ summary_markdown, revision_count, revision_ids, project_name }) {
  const res = await fetch(`${API_URL}/revisions/multi-summary/export-pdf`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ summary_markdown, revision_count, revision_ids, project_name }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(detail || 'PDF-export misslyckades');
  }
  return await res.blob();
}

export async function exportBoardReportPdf(revisionIds, includeOpen = false) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 180_000);
  try {
    const res = await fetch(`${API_URL}/revisions/multi-summary/board-report-pdf`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ revision_ids: revisionIds, include_open: !!includeOpen }),
      signal: controller.signal,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(detail || 'Styrelserapport misslyckades');
    }
    return await res.blob();
  } finally {
    clearTimeout(timer);
  }
}

export async function exportBoardReportHtml(revisionIds, includeOpen = false) {
  const controller = new AbortController();
  // KanAI-analysen tar 1-3 min vid många revisioner (187 s i prod 2026-10-27).
  // Strax under lastbalanserarens idle-timeout på 300 s.
  const timer = setTimeout(() => controller.abort(), 290_000);
  try {
    const res = await fetch(`${API_URL}/revisions/multi-summary/board-report-html`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ revision_ids: revisionIds, include_open: !!includeOpen }),
      signal: controller.signal,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(detail || 'Styrelserapport HTML misslyckades');
    }
    return await res.blob();
  } finally {
    clearTimeout(timer);
  }
}

export async function exportRevisionPdf(revisionId) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 120_000);
  try {
    const res = await fetch(`${API_URL}/revisions/${revisionId}/export-pdf`, { method: 'POST', credentials: 'include', headers: addTenantHeader(), signal: controller.signal });
    if (!res.ok) throw new Error('Failed to export PDF');
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchProtocolPreview(revisionId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/protocol-preview`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch protocol preview');
  return await res.json();
}

export async function fetchRevisionRespondData(revisionId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/respond-data`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch respond data');
  return await res.json();
}

export async function generateProtocolSummary(revisionId) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 90_000);
  try {
    const res = await fetch(`${API_URL}/revisions/${revisionId}/generate-protocol-summary`, {
      method: 'POST', credentials: 'include', headers: addTenantHeader(), signal: controller.signal,
    });
    if (!res.ok) throw new Error('Failed to generate protocol summary');
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

export async function generateProtocolPositives(revisionId) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 90_000);
  try {
    const res = await fetch(`${API_URL}/revisions/${revisionId}/generate-protocol-positives`, {
      method: 'POST', credentials: 'include', headers: addTenantHeader(), signal: controller.signal,
    });
    if (!res.ok) throw new Error('Failed to generate protocol positives');
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

export async function exportProtocolCustom(revisionId, { summary, scope_notes }) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/export-protocol-custom`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ summary, scope_notes }),
  });
  if (!res.ok) throw new Error('Failed to generate protocol PDF');
  return await res.json();
}

export async function sendRevisionForResponse(revisionId, { skipEmail = false } = {}) {
  const qs = skipEmail ? '?skip_email=true' : '';
  const res = await fetch(`${API_URL}/revisions/${revisionId}/send-for-response${qs}`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Failed to send revision for response';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function sendRevisionReminder(revisionId, participantIds = null) {
  const body = participantIds && participantIds.length > 0
    ? JSON.stringify({ participant_ids: participantIds })
    : undefined;
  const res = await fetch(`${API_URL}/revisions/${revisionId}/send-reminder`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...addTenantHeader(), ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body } : {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skicka påminnelse';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function sendRevisionProtocol(revisionId, participantIds = null) {
  const body = participantIds && participantIds.length > 0
    ? JSON.stringify({ participant_ids: participantIds })
    : undefined;
  const res = await fetch(`${API_URL}/revisions/${revisionId}/send-protocol`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...addTenantHeader(), ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body } : {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skicka slutprotokoll';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function generateParticipantToken(revisionId, participantId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/participants/${participantId}/generate-token`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Failed to generate token';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function generateAdhocResponseLink(revisionId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/generate-adhoc-link`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Failed to generate link';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function respondToRevisionFinding(findingId, patch) {
  const res = await fetch(`${API_URL}/revisions/revision-findings/${findingId}/response`, {
    method: 'PATCH',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(patch || {}),
  });
  if (!res.ok) {
    let msg = 'Failed to respond to finding';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function reviewFindingResponse(findingId, payload) {
  const res = await fetch(`${API_URL}/revisions/revision-findings/${findingId}/review`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    let msg = 'Failed to review finding response';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function uploadRevisionFindingResponseFile(findingId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/revisions/revision-findings/${findingId}/response-files`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: fd,
  });
  if (!res.ok) {
    let msg = 'Failed to upload response attachment';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function uploadRevisionPointResponseFile(pointId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/revisions/revision-points/${pointId}/response-files`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: fd,
  });
  if (!res.ok) {
    let msg = 'Failed to upload point attachment';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail);
    } catch {
      try {
        msg = await res.text();
      } catch {}
    }
    throw new Error(msg);
  }
  return await res.json();
}

export async function signRevisionAsAuditor(revisionId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/sign/auditor`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Failed to sign as auditor';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function signRevisionAsAudited(revisionId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/sign/audited`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Failed to sign as audited';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function uploadRevisionResponseDocument(revisionId, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_URL}/revisions/${revisionId}/response-document`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
    body: form,
  });
  if (!res.ok) {
    let msg = 'Failed to upload response document';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

export async function getDecisionProposals(revisionId, focus = '', maxItems = 3) {
  const body = { focus: focus || null, max_items: maxItems };
  const res = await fetch(`${API_URL}/revisions/${revisionId}/decision-proposals`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error('Failed to get decision proposals');
  return await res.json(); // { proposals: [...] }
}

// Analyze free text into suggested checklist points and findings (non-persistent)
export async function analyzeRevisionFreeText(revisionId, { text, max_points = 10, max_findings = 10 } = {}) {
  const body = { text: text || '', max_points, max_findings };
  const res = await fetch(`${API_URL}/revisions/${revisionId}/free-text-analyze`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    let msg = 'Failed to analyze free text';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json(); // { revision_id, suggestions: { points, findings }, meta }
}

// --- Revision Participants API ---
export async function listRevisionParticipants(revisionId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/participants`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to list participants');
  return await res.json();
}

export async function addRevisionParticipant(revisionId, body) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/participants`, {
    method: 'POST', credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = 'Failed to add participant';
    try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function updateRevisionParticipant(revisionId, participantId, body) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/participants/${participantId}`, {
    method: 'PATCH', credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to update participant');
  return await res.json();
}

export async function deleteRevisionParticipant(revisionId, participantId) {
  const res = await fetch(`${API_URL}/revisions/${revisionId}/participants/${participantId}`, {
    method: 'DELETE', credentials: 'include', headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to delete participant');
  return { ok: true };
}

// --- Public Revision Response API (token-based, no auth) ---
export async function publicGetRevisionForResponse(token) {
  const res = await fetch(`${API_URL}/api/public/revisions/respond/${token}`);
  if (!res.ok) {
    let msg = 'Ogiltig eller utgången länk';
    try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function publicRespondToFinding(token, findingId, body) {
  const res = await fetch(`${API_URL}/api/public/revisions/respond/${token}/findings/${findingId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = 'Kunde inte spara svar';
    try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function publicUploadFindingFile(token, findingId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/api/public/revisions/respond/${token}/findings/${findingId}/files`, {
    method: 'POST', body: fd,
  });
  if (!res.ok) {
    let msg = 'Kunde inte ladda upp fil';
    try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function publicDeleteFindingFile(token, fileId) {
  const res = await fetch(`${API_URL}/api/public/revisions/respond/${token}/files/${fileId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    let msg = 'Kunde inte ta bort fil';
    try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch {}
    throw new Error(msg);
  }
}

export async function publicSubmitRevisionResponse(token) {
  const res = await fetch(`${API_URL}/api/public/revisions/respond/${token}/submit`, { method: 'POST' });
  if (!res.ok) {
    let msg = 'Kunde inte skicka in svar';
    try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Project Map API ---
export async function fetchProjectMapProjects(params = {}) {
  const usp = new URLSearchParams();
  if (params.q) usp.set('q', params.q);
  (params.status || []).forEach(s => usp.append('status', s));
  (params.department || []).forEach(d => usp.append('department', d));
  if (params.active_during_start) usp.set('active_during_start', params.active_during_start);
  if (params.active_during_end) usp.set('active_during_end', params.active_during_end);
  (params.audit_status || []).forEach(a => usp.append('audit_status', a));
  if (params.min_audit_age_days) usp.set('min_audit_age_days', params.min_audit_age_days);
  if (params.bbox) usp.set('bbox', params.bbox); // minLat,minLng,maxLat,maxLng
  const res = await fetch(`${API_URL}/project-map/projects?${usp.toString()}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch project map data');
  return await res.json();
}

export async function fetchProjectMapMeta() {
  const res = await fetch(`${API_URL}/project-map/meta`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch project map meta');
  return await res.json();
}

export async function fetchProjectMapTimeline(params = {}) {
  const usp = new URLSearchParams();
  if (params.bucket) usp.set('bucket', params.bucket);
  if (params.start) usp.set('start', params.start);
  if (params.end) usp.set('end', params.end);
  (params.status || []).forEach(s => usp.append('status', s));
  (params.department || []).forEach(d => usp.append('department', d));
  (params.client || []).forEach(v => usp.append('client', v));
  (params.manager || []).forEach(v => usp.append('manager', v));
  (params.audit_status || []).forEach(a => usp.append('audit_status', a));
  if (params.min_audit_age_days) usp.set('min_audit_age_days', params.min_audit_age_days);
  if (params.q) usp.set('q', params.q);
  const res = await fetch(`${API_URL}/project-map/timeline?${usp.toString()}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch project map timeline');
  return await res.json();
}

export async function fetchResourceLayer(layer) {
  const res = await fetch(`${API_URL}/project-map/resources?layer=${encodeURIComponent(layer)}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch resource layer');
  return await res.json();
}

export async function fetchNearby(projectId, radiusKm=50) {
  const res = await fetch(`${API_URL}/project-map/nearby?project_id=${projectId}&radius_km=${radiusKm}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch nearby projects');
  return await res.json();
}

export async function aiProjectMapQuery(query) {
  const res = await fetch(`${API_URL}/project-map/ai/query`, {
    method: 'POST', credentials: 'include', headers: addTenantHeader({ 'Content-Type': 'application/json' }), body: JSON.stringify({ query })
  });
  if (!res.ok) throw new Error('Failed AI query');
  return await res.json();
}

// Fetch single project (needed for DocumentsModule to hämta platschef/manager)
export async function getProject(projectId) {
  const res = await fetch(`${API_URL}/projects/${projectId}`, {
    credentials: 'include',
    headers: addTenantHeader({}),
  });
  if (!res.ok) throw new Error('Failed to fetch project');
  return await res.json();
}

// --- Buyer View (Published data) ---
export async function fetchBuyerView(projectId) {
  const res = await fetch(`${API_URL}/projects/${projectId}/published/buyer-view`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch buyer view');
  return await res.json();
}

export async function createProject(project) {
  const res = await fetch(`${API_URL}/projects/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(project),
  });
  if (!res.ok) throw new Error("Failed to create project");
  return await res.json();
}

// --- Users (peers for selection) ---
export async function listUserPeers(query = '') {
  const usp = new URLSearchParams();
  if (query && query.trim()) usp.set('q', query.trim());
  const res = await fetch(`${API_URL}/users/peers${usp.toString() ? `?${usp.toString()}` : ''}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch user peers');
  return await res.json();
}

// --- Management System Admin ---
export async function fetchManagementValidationReport() {
  const res = await fetch(`${API_URL}/management-system/validation-report`);
  if (!res.ok) throw new Error('Failed to fetch validation report');
  return await res.json();
}

// --- Contract Notices (URE/URB) API ---
export async function listContractNotices(projectId) {
  const res = await apiFetch(`${API_URL}/contract-notices/project/${projectId}`);
  if (!res.ok) throw new Error("Failed to list contract notices");
  return await res.json();
}

export async function createContractNotice(projectId, body) {
  const res = await apiFetch(`${API_URL}/contract-notices/project/${projectId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to create contract notice");
  return await res.json();
}

export async function getContractNotice(noticeId) {
  const res = await apiFetch(`${API_URL}/contract-notices/${noticeId}`);
  if (!res.ok) throw new Error("Failed to get contract notice");
  return await res.json();
}

export async function updateContractNotice(noticeId, patch) {
  const res = await apiFetch(`${API_URL}/contract-notices/${noticeId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error("Failed to update contract notice");
  return await res.json();
}

export async function deleteContractNotice(noticeId) {
  const res = await apiFetch(`${API_URL}/contract-notices/${noticeId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete contract notice");
  return await res.json();
}

export async function sendContractNotice(noticeId, body = {}) {
  const res = await apiFetch(`${API_URL}/contract-notices/${noticeId}/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to send contract notice");
  return await res.json();
}

export async function confirmContractNoticeReceipt(noticeId, body = {}) {
  const res = await apiFetch(`${API_URL}/contract-notices/${noticeId}/confirm-receipt`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to confirm receipt");
  return await res.json();
}

export async function disputeContractNotice(noticeId, body = {}) {
  const res = await apiFetch(`${API_URL}/contract-notices/${noticeId}/dispute`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to dispute notice");
  return await res.json();
}

export async function closeContractNotice(noticeId, body = {}) {
  const res = await apiFetch(`${API_URL}/contract-notices/${noticeId}/close`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to close notice");
  return await res.json();
}

export async function listNoticesForChange(changeId) {
  const res = await apiFetch(`${API_URL}/contract-notices/for-change/${changeId}`);
  if (!res.ok) throw new Error("Failed to list notices for change");
  return await res.json();
}

export async function listAvailableNoticesForChange(projectId, changeId = null) {
  const url = changeId
    ? `${API_URL}/contract-notices/project/${projectId}/available-for-change?change_id=${changeId}`
    : `${API_URL}/contract-notices/project/${projectId}/available-for-change`;
  const res = await apiFetch(url);
  if (!res.ok) throw new Error("Failed to list available notices");
  return await res.json();
}

export async function linkNoticeToChange(changeId, noticeId) {
  const res = await apiFetch(`${API_URL}/contract-notices/link-to-change/${changeId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ notice_id: noticeId }),
  });
  if (!res.ok) throw new Error("Failed to link notice to change");
  return await res.json();
}

export async function unlinkNoticeFromChange(linkId) {
  const res = await apiFetch(`${API_URL}/contract-notices/link/${linkId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to unlink notice");
  return await res.json();
}

export function getContractNoticePdfUrl(noticeId, download = false) {
  return `${API_URL}/contract-notices/${noticeId}/pdf${download ? '?dl=1' : ''}`;
}

export async function getContractNoticeEmailInfo(noticeId) {
  const res = await apiFetch(`${API_URL}/contract-notices/${noticeId}/email-info`);
  if (!res.ok) throw new Error("Failed to get email info");
  return await res.json();
}

export async function sendContractNoticeEmail(noticeId) {
  const res = await apiFetch(`${API_URL}/contract-notices/${noticeId}/send-email`, {
    method: "POST",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || "Failed to send email");
  }
  return await res.json();
}

// --- Changes (ÄTA) API ---
export async function createChange(projectId, body) {
  const res = await apiFetch(`${API_URL}/changes/project/${projectId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to create ÄTA");
  return await res.json();
}

export async function listChanges(projectId) {
  const res = await apiFetch(`${API_URL}/changes/project/${projectId}`);
  if (!res.ok) throw new Error("Failed to list ÄTA");
  return await res.json();
}
 
export async function getChange(changeId) {
  const res = await apiFetch(`${API_URL}/changes/${changeId}`);
  if (!res.ok) throw new Error("Failed to get change");
  return res.json();
}

export async function listChangeLines(changeId) {
  const res = await apiFetch(`${API_URL}/changes/${changeId}/lines`);
  if (!res.ok) throw new Error("Failed to list change lines");
  return res.json();
}

export async function listChangeNotes(changeId) {
  const res = await apiFetch(`${API_URL}/changes/${changeId}/notes`);
  if (!res.ok) throw new Error("Failed to list change notes");
  return res.json();
}

export async function getChangeTotals(changeId) {
  const res = await apiFetch(`${API_URL}/changes/${changeId}/totals`);
  if (!res.ok) throw new Error("Failed to get change totals");
  return res.json();
}

export async function getChangeInternalCostByMerCode(projectId) {
  const res = await apiFetch(`${API_URL}/changes/project/${projectId}/internal-cost-by-mercode`);
  if (!res.ok) throw new Error("Failed to get ÄTA internal cost by MER-kod");
  return res.json();
}

// --- Payment Plan API ---
export async function getOrCreatePaymentPlan(projectId) {
  const res = await fetch(`${API_URL}/payment-plans/project/${projectId}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error("Failed to get payment plan");
  return await res.json();
}

export async function updatePaymentPlan(planId, patch) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}`, {
    method: "PUT",
    credentials: 'include',
    headers: addTenantHeader({ "Content-Type": "application/json" }),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error("Failed to update payment plan");
  return await res.json();
}

export async function listActivities(planId) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/activities`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error("Failed to list activities");
  return await res.json();
}

export async function addActivity(planId, body) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/activities`, {
    method: "POST",
    credentials: 'include',
    headers: addTenantHeader({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to add activity");
  return await res.json();
}

export async function updateActivity(planId, activityId, patch) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/activities/${activityId}`, {
    method: "PUT",
    credentials: 'include',
    headers: addTenantHeader({ "Content-Type": "application/json" }),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error("Failed to update activity");
  return await res.json();
}

export async function deleteActivity(planId, activityId) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/activities/${activityId}`, { method: "DELETE", credentials: 'include', headers: addTenantHeader() });
  if (!res.ok && res.status !== 204) throw new Error("Failed to delete activity");
}

export async function listLifts(planId) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/lifts`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error("Failed to list lifts");
  return await res.json();
}

export async function addLift(planId, body) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/lifts`, {
    method: "POST",
    credentials: 'include',
    headers: addTenantHeader({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to add lift");
  return await res.json();
}

export async function updateLift(planId, liftId, patch) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/lifts/${liftId}`, {
    method: "PUT",
    credentials: 'include',
    headers: addTenantHeader({ "Content-Type": "application/json" }),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error("Failed to update lift");
  return await res.json();
}

export async function deleteLift(planId, liftId) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/lifts/${liftId}`, { method: "DELETE", credentials: 'include', headers: addTenantHeader() });
  if (!res.ok && res.status !== 204) throw new Error("Failed to delete lift");
}

export async function upsertAllocation(planId, body) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/allocations`, {
    method: "POST",
    credentials: 'include',
    headers: addTenantHeader({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to upsert allocation");
  return await res.json();
}

export async function bulkAllocations(planId, items) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/allocations/bulk`, {
    method: "POST",
    credentials: 'include',
    headers: addTenantHeader({ "Content-Type": "application/json" }),
    body: JSON.stringify(items),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const data = await res.json();
      detail = data?.detail || (typeof data === 'string' ? data : JSON.stringify(data));
    } catch {
      try { detail = await res.text(); } catch { /* ignore */ }
    }
    const msg = detail ? `Failed to bulk upsert allocations: ${detail}` : 'Failed to bulk upsert allocations';
    throw new Error(`[${res.status}] ${msg}`);
  }
  return await res.json();
}

export async function lockAllocation(planId, body) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/allocations/lock`, {
    method: "POST",
    credentials: 'include',
    headers: addTenantHeader({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to lock allocation");
  return await res.json();
}

export async function listPlanEvents(planId, { activityId, liftId, limit } = {}) {
  const params = new URLSearchParams();
  if (activityId) params.set('activity_id', activityId);
  if (liftId) params.set('lift_id', liftId);
  if (limit != null) params.set('limit', String(limit));
  const res = await fetch(`${API_URL}/payment-plans/${planId}/events?${params.toString()}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error("Failed to fetch events");
  return await res.json();
}

export async function getMatrix(planId) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/matrix`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error("Failed to fetch matrix");
  return await res.json();
}

export async function getSummary(planId) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/summary`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error("Failed to fetch summary");
  return await res.json();
}

export function getPdfUrl(planId) {
  return `${API_URL}/payment-plans/${planId}/pdf`;
}

export async function downloadPlanPdf(planId, orientation = 'landscape') {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/pdf?orientation=${orientation}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to download PDF');
  return await res.blob();
}

export async function syncChanges(planId) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/sync-changes`, { method: "POST", credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error("Failed to sync changes into plan");
  return await res.json();
}

export async function syncChangesToActivities(planId, { perChangeActivity = true, actor } = {}) {
  const params = new URLSearchParams();
  if (perChangeActivity != null) params.set('per_change_activity', String(perChangeActivity));
  if (actor) params.set('actor', actor);
  const res = await fetch(`${API_URL}/payment-plans/${planId}/sync-changes-to-activities?${params.toString()}`, {
    method: "POST",
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error("Failed to sync ÄTA to activities");
  return await res.json();
}

// Reconcile endpoints
export async function reconcilePreview(planId, body) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/reconcile/preview`, {
    method: "POST",
    credentials: 'include',
    headers: addTenantHeader({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const msg = await res.text().catch(()=>"");
    throw new Error(msg || "Failed to preview reconcile");
  }
  return await res.json();
}

export async function reconcileApply(planId, body) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/reconcile`, {
    method: "POST",
    credentials: 'include',
    headers: addTenantHeader({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const msg = await res.text().catch(()=>"");
    throw new Error(msg || "Failed to apply reconcile");
  }
  return await res.json();
}

export async function createPlanEvent(planId, payload) {
  const res = await fetch(`${API_URL}/payment-plans/${planId}/events`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) throw new Error('Failed to create event');
  return await res.json();
}

// --- Documents (Phase 1A) ---
const docFetch = (url, options = {}) => (
  fetch(url, {
    ...options,
    credentials: options.credentials ?? 'include',
    headers: addTenantHeader(options.headers || {}),
  })
);
export async function listDocumentTree(projectId, { includeDeleted=false } = {}) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/tree?include_deleted=${includeDeleted}`);
  if (!res.ok) throw new Error('Failed to fetch document tree');
  return await res.json();
}

export async function createDocumentFolder(projectId, { name, parent_id }) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/folders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, parent_id: parent_id || null }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(()=>'' );
    throw new Error(detail || 'Failed to create folder');
  }
  return await res.json();
}

export async function uploadDocuments(projectId, folderId, files) {
  const attempt = async (attemptNo) => {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    if (folderId) form.append('folder_id', folderId);
    const controller = new AbortController();
    // Scale timeout with payload size: 30s base + 8s per MB + escalation per retry
    const totalBytes = files.reduce((sum, f) => sum + (f.size || 0), 0);
    const sizeMB = totalBytes / (1024 * 1024);
    const timeoutMs = Math.max(30000, 30000 + sizeMB * 8000) + (attemptNo * 5000);
    const tHandle = setTimeout(()=> controller.abort(), timeoutMs);
    let res;
    try {
      res = await docFetch(`${API_URL}/projects/${projectId}/documents/upload`, { method: 'POST', body: form, signal: controller.signal });
    } catch (netErr) {
      clearTimeout(tHandle);
      if (controller.signal.aborted) throw new Error('Network timeout');
      throw new Error('Network error (could not reach server)');
    }
    clearTimeout(tHandle);
    if (!res.ok) {
      if (res.status === 409) {
        try {
          const data = await res.json();
          const detail = data?.detail || data;
            const err = new Error(detail?.code || 'conflict');
            // Attach structured conflict detail (FastAPI wraps inside detail)
            err.conflictDetail = detail;
            err.status = 409;
            throw err;
        } catch(parseErr) {
          const err = new Error('conflict'); err.status=409; throw err;
        }
      }
      const text = await res.text().catch(()=> '');
      if (res.status >= 500) throw new Error(text || `Server error ${res.status}`);
      throw new Error(text || 'Failed to upload');
    }
    try { return await res.json(); } catch { return { status: 'ok'}; }
  };
  const maxRetries = 4;
  let lastErr;
  for (let i=0;i<=maxRetries;i++) {
    try { return await attempt(i); } catch (err) {
      lastErr = err;
      const msg = (err && err.message) ? err.message : '';
      if (!/(Network|Server error|timeout|502|503|504)/i.test(msg) || i===maxRetries) {
        throw err;
      }
      const base = 500 * Math.pow(2, i); // 500,1000,2000,4000...
      const jitter = base * (0.25 * Math.random());
      await new Promise(r=> setTimeout(r, base + jitter));
    }
  }
  throw lastErr || new Error('Failed to upload');
}

export async function uploadNewVersion(projectId, documentId, file, comment, { ifMatch } = {}) {
  const form = new FormData();
  form.append('file', file);
  if (comment) form.append('comment', comment);
  const headers = {};
  if (ifMatch) headers['If-Match'] = ifMatch;
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/versions`, { method: 'POST', body: form, headers });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to create version'));
  return await res.json();
}

export async function renameDocument(projectId, documentId, newName, { ifMatch } = {}) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(ifMatch ? { 'If-Match': ifMatch } : {}) },
    body: JSON.stringify({ new_name: newName })
  });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to rename document'));
  return await res.json();
}

export async function renameFolder(projectId, folderId, newName, { ifMatch } = {}) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/folders/${folderId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(ifMatch ? { 'If-Match': ifMatch } : {}) },
    body: JSON.stringify({ new_name: newName })
  });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to rename folder'));
  return await res.json();
}

export function documentDownloadUrl(projectId, documentId) {
  return `${API_URL}/projects/${projectId}/documents/${documentId}/download`;
}

// --- Drawings (Ritningar) ---
export async function listDrawings(projectId, { q, discipline, doc_type, status } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (discipline) params.set('discipline', discipline);
  if (doc_type) params.set('doc_type', doc_type);
  if (status) params.set('status', status);
  const qs = params.toString();
  const res = await apiFetch(`${API_URL}/projects/${projectId}/drawings${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to list drawings'));
  return await res.json();
}

export async function getDrawing(projectId, drawingId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/drawings/${drawingId}`);
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to get drawing'));
  return await res.json();
}

export async function createDrawing(projectId, payload) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/drawings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to create drawing'));
  return await res.json();
}

export async function addDrawingRevision(projectId, drawingId, payload) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/drawings/${drawingId}/revisions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to add revision'));
  return await res.json();
}

export async function expireDrawingRevision(projectId, drawingId, revisionId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/drawings/${drawingId}/revisions/${revisionId}/expire`, {
    method: 'POST'
  });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to expire revision'));
  return await res.json();
}

export async function updateDrawing(projectId, drawingId, payload) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/drawings/${drawingId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to update drawing'));
  return await res.json();
}

export async function deleteDrawing(projectId, drawingId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/drawings/${drawingId}`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to delete drawing'));
}

export async function listDocumentVersions(projectId, documentId) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/versions`);
  if (!res.ok) throw new Error('Failed to list versions');
  return await res.json();
}

// Phase 2
export async function restoreDocumentVersion(projectId, documentId, versionNumber, { ifMatch } = {}) {
  const headers = { ...(ifMatch ? { 'If-Match': ifMatch } : {}) };
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/versions/${versionNumber}/restore`, { method: 'POST', headers });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to restore version'));
  return await res.json();
}

export async function moveDocument(projectId, documentId, targetFolderId, { ifMatch } = {}) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/move`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(ifMatch ? { 'If-Match': ifMatch } : {}) },
    body: JSON.stringify({ target_folder_id: targetFolderId || null })
  });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to move document'));
  return await res.json();
}

export async function moveFolder(projectId, folderId, targetParentId, { ifMatch } = {}) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/folders/${folderId}/move`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(ifMatch ? { 'If-Match': ifMatch } : {}) },
    body: JSON.stringify({ target_parent_id: targetParentId || null })
  });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to move folder'));
  return await res.json();
}

export async function listDocumentEvents(projectId, limit=50) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/events?limit=${limit}`);
  if (!res.ok) throw new Error('Failed to fetch document events');
  return await res.json();
}

// Phase 1.5 extension: soft delete & folder delete
/** Makulera (default) eller radera ett dokument.
 *  kind: 'makulerad' bevaras for alltid som historik, 'borttagen' hamnar i
 *  papperskorgen och gallras efter 30 dagar (Mikael 2026-09-11). */
export async function softDeleteDocument(projectId, documentId, { ifMatch, kind = 'makulerad' } = {}) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}?kind=${encodeURIComponent(kind)}`, { method: 'DELETE', headers: { ...(ifMatch ? { 'If-Match': ifMatch } : {}) } });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to delete document'));
  return await res.json();
}

export async function undeleteDocument(projectId, documentId, { ifMatch } = {}) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/undelete`, { method: 'POST', headers: { ...(ifMatch ? { 'If-Match': ifMatch } : {}) } });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to undelete document'));
  return await res.json();
}

export async function deleteFolder(projectId, folderId, { force=false, hard=false, cascade=false, kind='makulerad' } = {}) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/folders/${folderId}?force=${force}&hard=${hard}&cascade=${cascade}&kind=${encodeURIComponent(kind)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to delete folder'));
  return await res.json();
}

export async function undeleteFolder(projectId, folderId, { cascade=false } = {}) {
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/folders/${folderId}/undelete?cascade=${cascade}`, { method: 'POST' });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to undelete folder'));
  return await res.json();
}

// --- Document tags & locking ---
export async function listDocumentTags(projectId, documentId){
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/tags`);
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to list tags'));
  return await res.json();
}

export async function addDocumentTag(projectId, documentId, tag){
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/tags`, {
    method: 'POST', headers: { 'Content-Type':'application/json' }, body: JSON.stringify({ tag })
  });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to add tag'));
  return await res.json();
}

export async function removeDocumentTag(projectId, documentId, tag){
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/tags/${encodeURIComponent(tag)}`, { method:'DELETE' });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to remove tag'));
  return await res.json();
}

export async function toggleFavoriteDocument(projectId, documentId){
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/favorite`, { method:'POST' });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to toggle favorite'));
  return await res.json();
}

export async function listAllDocumentTags(projectId){
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/all-tags`);
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to list all tags'));
  return await res.json();
}

export async function lockDocumentApi(projectId, documentId, actor){
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/lock`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ actor })});
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to lock document'));
  return await res.json();
}

// --- KanAI temporary uploads ---
export async function deleteKanaiTempDocument(projectId, documentId){
  const res = await docFetch(`${API_URL}/api/kanai/projects/${projectId}/temp-documents/${documentId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to delete KanAI temp document'));
  return await res.json();
}

export async function purgeKanaiTempDocuments(projectId, documentIds){
  const body = (Array.isArray(documentIds) && documentIds.length) ? { documentIds } : {};
  const res = await docFetch(`${API_URL}/api/kanai/projects/${projectId}/temp-documents/purge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to purge KanAI temp documents'));
  return await res.json();
}

export async function unlockDocumentApi(projectId, documentId, actor, { force=false } = {}){
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/${documentId}/unlock`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ actor, force })});
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to unlock document'));
  return await res.json();
}

export async function retentionPurge(projectId, { dryRun=true } = {}){
  const res = await docFetch(`${API_URL}/projects/${projectId}/documents/retention/purge?dry_run=${dryRun}` , { method:'POST' });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to run retention purge'));
  return await res.json();
}

export async function folderStats(projectId, folderId) {
  const res = await fetch(`${API_URL}/projects/${projectId}/documents/folders/${folderId}/stats`);
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to fetch folder stats'));
  return await res.json();
}

// Bulk create folders to reduce request storm during large directory uploads
export async function bulkCreateDocumentFolders(projectId, { paths, base_parent_id=null }) {
  const res = await fetch(`${API_URL}/projects/${projectId}/documents/folders/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paths, base_parent_id })
  });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to bulk create folders'));
  return await res.json();
}

export async function bulkPreviewDocumentFolders(projectId, { paths, base_parent_id=null }) {
  const res = await fetch(`${API_URL}/projects/${projectId}/documents/folders/bulk/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paths, base_parent_id })
  });
  if (!res.ok) throw new Error(await res.text().catch(()=> 'Failed to preview bulk folders'));
  return await res.json();
}

// --- Document templates (admin) ---
export async function adminListDocumentTemplates({ status, includeDrafts = true } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (includeDrafts !== undefined && includeDrafts !== null) {
    params.set('includeDrafts', includeDrafts ? 'true' : 'false');
  }
  const res = await fetch(`${API_URL}/admin/document-templates${params.toString() ? `?${params.toString()}` : ''}`, {
    credentials: 'include'
  });
  if (!res.ok) {
    const text = await res.text().catch(()=> '');
    throw new Error(text || 'Failed to list document templates');
  }
  return await res.json();
}

export async function adminGetDocumentTemplate(templateId) {
  const res = await fetch(`${API_URL}/admin/document-templates/${templateId}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(()=> '');
    throw new Error(text || 'Failed to fetch document template');
  }
  return await res.json();
}

export async function adminImportDocumentTemplate({ file, name, category, description, includeFiles = true, defaultTargetFolderHint, actor }) {
  if (!file) throw new Error('file is required');
  const form = new FormData();
  form.append('file', file);
  if (name) form.append('name', name);
  if (category) form.append('category', category);
  if (description) form.append('description', description);
  form.append('include_files', includeFiles ? 'true' : 'false');
  if (defaultTargetFolderHint) form.append('default_target_folder_hint', defaultTargetFolderHint);
  if (actor) form.append('actor', actor);
  const res = await fetch(`${API_URL}/admin/document-templates/import`, {
    method: 'POST',
    body: form,
    credentials: 'include'
  });
  if (!res.ok) {
    const text = await res.text().catch(()=> '');
    throw new Error(text || 'Failed to import document template');
  }
  return await res.json();
}

async function adminTemplateAction(templateId, path, { actor } = {}) {
  const form = new FormData();
  if (actor) form.append('actor', actor);
  const res = await fetch(`${API_URL}/admin/document-templates/${templateId}/${path}`, {
    method: 'POST',
    body: form,
    credentials: 'include'
  });
  if (!res.ok) {
    const text = await res.text().catch(()=> '');
    throw new Error(text || `Failed to ${path} template`);
  }
  return await res.json();
}

export function adminPublishDocumentTemplate(templateId, options) {
  return adminTemplateAction(templateId, 'publish', options);
}

export function adminArchiveDocumentTemplate(templateId, options) {
  return adminTemplateAction(templateId, 'archive', options);
}

export async function adminDeleteDocumentTemplate(templateId, { actor, force = false } = {}) {
  const params = new URLSearchParams();
  if (actor) params.set('actor', actor);
  if (force) params.set('force', force ? 'true' : 'false');
  const query = params.toString();
  const res = await fetch(`${API_URL}/admin/document-templates/${templateId}${query ? `?${query}` : ''}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!res.ok) {
    const text = await res.text().catch(()=> '');
    throw new Error(text || 'Failed to delete document template');
  }
  return true;
}

// --- Document templates (project/apply) ---
export async function listAvailableDocumentTemplates(projectId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/documents/templates/available`);
  if (!res.ok) {
    const text = await res.text().catch(()=> '');
    throw new Error(text || 'Failed to list available templates');
  }
  return await res.json();
}

export async function dryRunDocumentTemplate(projectId, templateId, payload) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/documents/templates/${templateId}/dry-run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) {
    const text = await res.text().catch(()=> '');
    throw new Error(text || 'Failed to simulate template apply');
  }
  return await res.json();
}

export async function applyDocumentTemplate(projectId, templateId, payload) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/documents/templates/${templateId}/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {})
  });
  if (!res.ok) {
    const text = await res.text().catch(()=> '');
    throw new Error(text || 'Failed to apply template');
  }
  return await res.json();
}

// --- Document library ---

// --- Budget & Forecast (Sprint A1/A2) ---
export async function listBudgetVersions(projectId) {
  const res = await fetch(`${API_URL}/projects/${projectId}/budget/versions`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to list budget versions';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function createBudgetVersion(projectId, body) {
  const res = await fetch(`${API_URL}/projects/${projectId}/budget/versions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Failed to create budget version';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function lockBudgetVersion(projectId, versionId, actor) {
  const qs = actor ? `?actor=${encodeURIComponent(actor)}` : '';
  const res = await fetch(`${API_URL}/projects/${projectId}/budget/versions/${versionId}/lock${qs}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = 'Failed to lock budget version';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export function getBudgetCsvUrl(projectId, versionId) {
  return `${API_URL}/projects/${projectId}/budget/versions/${versionId}/export.csv`;
}

export async function listBudgetLines(projectId, versionId) {
  const res = await fetch(`${API_URL}/projects/${projectId}/budget/versions/${versionId}/lines`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to list budget lines';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function addBudgetLine(projectId, versionId, body) {
  const res = await fetch(`${API_URL}/projects/${projectId}/budget/versions/${versionId}/lines`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let detail = 'Failed to add budget line';
    try { const data = await res.json(); if (data?.detail) detail = data.detail; } catch {}
    const err = new Error(detail); err.status = res.status; throw err;
  }
  return await res.json();
}

export async function importBudgetCsv(projectId, versionId, file, { dryRun = true } = {}) {
  const form = new FormData();
  form.append('file', file);
  form.append('dry_run', String(dryRun));
  const res = await fetch(`${API_URL}/projects/${projectId}/budget/versions/${versionId}/import`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    let msg = 'Failed to import CSV';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function purgeProjectBudgetAndForecasts(projectId) {
  const res = await fetch(`${API_URL}/projects/${projectId}/budget/purge?confirm=true`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte rensa budget/prognos';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail) || msg; } catch {}
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return await res.json();
}

// --- ByggDialog och projektkalkyl (dataset) ---

export async function getByggDialogDataset(projectId, period, { thresholdPct } = {}) {
  const usp = new URLSearchParams();
  if (thresholdPct !== undefined && thresholdPct !== null && thresholdPct !== '') {
    usp.set('threshold_pct', String(thresholdPct));
  }
  const qs = usp.toString() ? `?${usp.toString()}` : '';

  const res = await fetch(`${API_URL}/projects/${projectId}/byggdialog/${period}${qs}`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta ByggDialog-data';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function getProjektkalkylDataset(projectId, period) {
  const res = await fetch(`${API_URL}/projects/${projectId}/projektkalkyl/${period}`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) {
    let msg = 'Kunde inte hämta Projektkalkyl-data';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// --- Budget ↔ KanCalc bridge ---

export async function ensureBudgetLineCalcRow(projectId, budgetVersionId, budgetLineId) {
  const url = `${API_URL}/projects/${encodeURIComponent(projectId)}/budget/versions/${encodeURIComponent(budgetVersionId)}/lines/${encodeURIComponent(budgetLineId)}/ensure-calc-row`;
  const res = await fetch(url, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Kunde inte skapa/koppla budgetrad till KanCalc';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function listForecastVersions(projectId) {
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) {
    let msg = 'Failed to list forecast versions';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function createForecastVersion(projectId, body) {
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions`, {
    method: 'POST',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Failed to create forecast version';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function listForecastLines(projectId, versionId) {
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions/${versionId}/lines`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to list forecast lines');
  return await res.json();
}

export async function seedForecastFromBudget(projectId, versionId, budgetVersionId) {
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions/${versionId}/seed-from-budget?budget_version_id=${encodeURIComponent(budgetVersionId)}`, { method: 'POST', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to seed forecast from budget');
  return await res.json();
}

export async function refreshBakFromCalc(projectId, versionId, { calcVersionId = null, apply = false } = {}) {
  const params = new URLSearchParams();
  if (calcVersionId) params.set('calc_version_id', calcVersionId);
  params.set('apply', apply ? 'true' : 'false');
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions/${versionId}/refresh-bak-from-calc?${params}`, {
    method: 'POST', credentials: 'include', headers: addTenantHeader()
  });
  if (!res.ok) throw new Error('Kunde inte uppdatera BÅK från kalkyl');
  return await res.json();
}

export async function applyActualsToForecast(projectId, versionId, { period, mode = 'category', apply = false } = {}) {
  const params = new URLSearchParams();
  params.set('period', period);
  params.set('mode', mode);
  params.set('apply', apply ? 'true' : 'false');
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions/${versionId}/apply-actuals?${params}`, {
    method: 'POST', credentials: 'include', headers: addTenantHeader()
  });
  if (!res.ok) throw new Error('Kunde inte applicera utfall till prognos');
  return await res.json();
}

export async function seedForecastFromPrevious(projectId, versionId, { sourcePeriod } = {}) {
  const params = new URLSearchParams();
  if (sourcePeriod) params.set('source_period', sourcePeriod);
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions/${versionId}/seed-from-previous?${params}`, {
    method: 'POST', credentials: 'include', headers: addTenantHeader()
  });
  if (!res.ok) throw new Error('Kunde inte rulla framåt prognos från föregående period');
  return await res.json();
}

export async function refreshBakModelSpecific(projectId, versionId, { calcVersionId, apply = false } = {}) {
  const params = new URLSearchParams();
  if (calcVersionId) params.set('calc_version_id', calcVersionId);
  params.set('apply', apply ? 'true' : 'false');
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions/${versionId}/refresh-bak-model-specific?${params}`, {
    method: 'POST', credentials: 'include', headers: addTenantHeader()
  });
  if (!res.ok) throw new Error('Kunde inte beräkna modellspecifik BÅK');
  return await res.json();
}

export async function getBakSummary(projectId) {
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/bak-summary`, {
    credentials: 'include', headers: addTenantHeader()
  });
  if (!res.ok) throw new Error('Kunde inte hämta BÅK-sammanfattning');
  return await res.json();
}

export async function updateForecastLine(projectId, lineId, patch) {
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/lines/${lineId}`, {
    method: 'PATCH', headers: addTenantHeader({ 'Content-Type': 'application/json' }), credentials: 'include', body: JSON.stringify(patch || {})
  });
  if (!res.ok) throw new Error('Failed to update forecast line');
  return await res.json();
}

export async function updateForecastVersionMetadata(projectId, versionId, patch) {
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions/${versionId}`, {
    method: 'PATCH', headers: addTenantHeader({ 'Content-Type': 'application/json' }), credentials: 'include', body: JSON.stringify(patch || {})
  });
  if (!res.ok) throw new Error('Kunde inte uppdatera prognosversion');
  return await res.json();
}

export async function submitForecastVersion(projectId, versionId, { actor, note } = {}) {
  const params = new URLSearchParams();
  if (actor) params.set('actor', actor);
  if (note) params.set('note', note);
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions/${versionId}/submit?${params.toString()}`, { method: 'POST', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to submit forecast');
  return await res.json();
}

export async function approveForecastVersion(projectId, versionId, { actor, note } = {}) {
  const params = new URLSearchParams();
  if (actor) params.set('actor', actor);
  if (note) params.set('note', note);
  const res = await fetch(`${API_URL}/projects/${projectId}/forecasts/versions/${versionId}/approve?${params.toString()}`, { method: 'POST', credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to approve forecast');
  return await res.json();
}

// --- Insight API ---
export async function listInsightItems({ q, tag, source, category, priority, featured_only, offset, limit } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (tag) params.set('tag', tag);
  if (source) params.set('source', source);
  if (category) params.set('category', category);
  if (priority) params.set('priority', priority);
  if (featured_only) params.set('featured_only', 'true');
  if (offset != null) params.set('offset', String(offset));
  if (limit != null) params.set('limit', String(limit));
  const res = await fetch(`${API_URL}/insight/items${params.toString() ? `?${params.toString()}` : ''}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to list insight items');
  return await res.json();
}

export async function getInsightItem(id) {
  const res = await fetch(`${API_URL}/insight/items/${id}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch insight item');
  return await res.json();
}

export async function markInsightRead(id, isRead = true) {
  const res = await fetch(`${API_URL}/insight/items/${id}/mark-read`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ is_read: !!isRead }),
  });
  if (!res.ok) throw new Error('Failed to mark insight as read');
  return await res.json();
}

export async function createInsightItem(body) {
  const res = await fetch(`${API_URL}/insight/items`, {
    method: 'POST',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte publicera nyhet';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function updateInsightItem(id, body) {
  const res = await fetch(`${API_URL}/insight/items/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let msg = 'Kunde inte uppdatera nyhet';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function deleteInsightItem(id) {
  const res = await fetch(`${API_URL}/insight/items/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader({ 'Content-Type': 'application/json' }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte radera nyhet';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function getInsightStats() {
  const res = await fetch(`${API_URL}/insight/stats`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch insight stats');
  return await res.json();
}

export async function getInsightTicker(limit = 10) {
  const params = limit ? `?limit=${limit}` : '';
  const res = await fetch(`${API_URL}/insight/ticker${params}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch insight ticker');
  return await res.json();
}

export async function getInsightTimeline(limit = 10) {
  const params = limit ? `?limit=${limit}` : '';
  const res = await fetch(`${API_URL}/insight/timeline${params}`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch insight timeline');
  return await res.json();
}

export async function getInsightSources() {
  const res = await fetch(`${API_URL}/insight/sources`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch insight sources');
  return await res.json();
}

export async function getInsightSourceRegistry() {
  const res = await fetch(`${API_URL}/insight/source-registry`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch insight source registry');
  return await res.json();
}

export async function fetchInsightSources(sourceKeys = null) {
  const body = sourceKeys ? { source_keys: sourceKeys } : {};
  const res = await fetch(`${API_URL}/insight/fetch-sources`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to fetch insight sources');
  return await res.json();
}

export async function getInsightFetchStatus() {
  const res = await fetch(`${API_URL}/insight/fetch-status`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to fetch insight fetch status');
  return await res.json();
}

// --- Insight Tenant Source Configuration ---
export async function listInsightTenantSources() {
  const res = await fetch(`${API_URL}/insight/tenant-sources`, { credentials: 'include', headers: addTenantHeader() });
  if (!res.ok) throw new Error('Failed to list tenant sources');
  return await res.json();
}

export async function addInsightTenantSource(body) {
  const res = await fetch(`${API_URL}/insight/tenant-sources`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to add tenant source');
  return await res.json();
}

export async function updateInsightTenantSource(id, body) {
  const res = await fetch(`${API_URL}/insight/tenant-sources/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to update tenant source');
  return await res.json();
}

export async function deleteInsightTenantSource(id) {
  const res = await fetch(`${API_URL}/insight/tenant-sources/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to delete tenant source');
  return await res.json();
}

export async function toggleInsightTenantSource(sourceKey, enabled) {
  const res = await fetch(`${API_URL}/insight/tenant-sources/toggle`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...addTenantHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ source_key: sourceKey, enabled }),
  });
  if (!res.ok) throw new Error('Failed to toggle tenant source');
  return await res.json();
}

export async function searchMfnCompanies(query) {
  const res = await fetch(`${API_URL}/insight/mfn-search?q=${encodeURIComponent(query)}`, {
    credentials: 'include',
    headers: addTenantHeader(),
  });
  if (!res.ok) throw new Error('Failed to search MFN companies');
  return await res.json();
}

// --- Hogia supplier invoices (MVP browsing) ---
export async function syncHogiaSupplierInvoices({ organization, page_size = 50, max_pages = 1, dry_run = false } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  const res = await fetch(`${API_URL}/hogia/sync/supplierinvoices?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to sync supplier invoices';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function syncHogiaAccounts({ organization, page_size = 50, max_pages = 1, dry_run = false } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  const res = await fetch(`${API_URL}/hogia/sync/accounts?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to sync accounts';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function syncHogiaSuppliers({ organization, page_size = 50, max_pages = 1, dry_run = false } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  const res = await fetch(`${API_URL}/hogia/sync/suppliers?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to sync suppliers';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function syncHogiaVouchers({ organization, page_size = 50, max_pages = 1, dry_run = false, force = false, financial_year_id = null } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  if (force) usp.set('force', 'true');
  if (financial_year_id) usp.set('financial_year_id', financial_year_id);
  const res = await fetch(`${API_URL}/hogia/sync/vouchers?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to sync vouchers';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function getHogiaFinancialYears({ organization } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  const res = await fetch(`${API_URL}/hogia/financial-years?${usp.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to fetch financial years';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function startHogiaVouchersBackground({ organization, financial_year_id, page_size = 200, force = true } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('page_size', String(page_size));
  if (force) usp.set('force', 'true');
  if (financial_year_id) usp.set('financial_year_id', financial_year_id);
  const res = await fetch(`${API_URL}/hogia/sync/vouchers/start?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to start vouchers sync';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function syncHogiaCurrencies({ organization, page_size = 50, max_pages = 1, dry_run = false } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  const res = await fetch(`${API_URL}/hogia/sync/currencies?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to sync currencies';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

// --- Hogia masterdata (Sprint 2) ---
export async function syncHogiaProjects({ organization, page_size = 50, max_pages = 1, dry_run = false } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  const res = await fetch(`${API_URL}/hogia/sync/projects?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to sync projects';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function syncHogiaProfitcenters({ organization, page_size = 50, max_pages = 1, dry_run = false } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  const res = await fetch(`${API_URL}/hogia/sync/profitcenters?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to sync profit centers';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function syncHogiaCostbearers({ organization, page_size = 50, max_pages = 1, dry_run = false } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  const res = await fetch(`${API_URL}/hogia/sync/costbearers?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to sync cost bearers';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function syncHogiaCustomers({ organization, page_size = 50, max_pages = 1, dry_run = false } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  const res = await fetch(`${API_URL}/hogia/sync/customers?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to sync customers';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function syncHogiaArticles({ organization, page_size = 50, max_pages = 1, dry_run = false } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  const res = await fetch(`${API_URL}/hogia/sync/articles?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to sync articles';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function syncHogiaBatch({ organization, page_size = 50, max_pages = 1, include_invoices = false, include_vouchers = false, dry_run = false } = {}) {
  if (!organization || !organization.trim()) throw new Error('Organization is required');
  const usp = new URLSearchParams();
  usp.set('organization', organization.trim());
  if (page_size != null) usp.set('page_size', String(page_size));
  if (max_pages != null) usp.set('max_pages', String(max_pages));
  usp.set('include_invoices', String(!!include_invoices));
  usp.set('include_vouchers', String(!!include_vouchers));
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  const res = await fetch(`${API_URL}/hogia/sync/batch?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to run batch sync';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaInvoices(params = {}) {
  const usp = new URLSearchParams();
  if (params.organization) usp.set('organization', params.organization);
  if (params.supplier_id) usp.set('supplier_id', params.supplier_id);
  if (params.q) usp.set('q', params.q);
  if (params.date_from) usp.set('date_from', params.date_from);
  if (params.date_to) usp.set('date_to', params.date_to);
  if (params.min_amount != null) usp.set('min_amount', String(params.min_amount));
  if (params.max_amount != null) usp.set('max_amount', String(params.max_amount));
  const page = params.page || 0;
  const pageSize = params.page_size || params.pageSize || 50;
  usp.set('page', String(page));
  usp.set('page_size', String(pageSize));
  const tenantSlug = getActiveTenantSlug();
  const headers = tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {};
  const res = await fetch(`${API_URL}/hogia/invoices?${usp.toString()}`, { credentials: 'include', headers });
  if (!res.ok) {
    let msg = 'Failed to list Hogia invoices';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail);
      else if (data?.error) msg = data.error;
    } catch {
      try { msg = await res.text(); } catch { /* ignore */ }
    }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json(); // { items, total, page, page_size }
}

export async function getHogiaInvoice(invoiceId, { organization } = {}) {
  const qs = new URLSearchParams();
  if (organization) qs.set('organization', organization);
  const url = `${API_URL}/hogia/invoices/${invoiceId}${qs.toString() ? `?${qs.toString()}` : ''}`;
  const tenantSlug = getActiveTenantSlug();
  const headers = tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {};
  const res = await fetch(url, { credentials: 'include', headers });
  if (!res.ok) {
    let msg = 'Failed to fetch invoice';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error;
    } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function getHogiaInvoiceImage(invoiceId, { organization } = {}) {
  const qs = new URLSearchParams();
  if (organization) qs.set('organization', organization);
  const url = `${API_URL}/hogia/invoices/${invoiceId}/image${qs.toString() ? `?${qs.toString()}` : ''}`;
  const tenantSlug = getActiveTenantSlug();
  const headers = tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {};
  const res = await fetch(url, { credentials: 'include', headers });
  if (res.status === 404) {
    // Graceful fallback: treat as "no image available" instead of throwing
    return { available: false, stub: false };
  }
  if (!res.ok) {
    let msg = 'Failed to fetch invoice image';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error;
    } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  const data = await res.json(); // { available, content_type, url, stub }
  // Ensure absolute URL so <img>/<iframe> load from backend rather than the frontend dev server
  if (data && typeof data.url === 'string' && data.url.startsWith('/')) {
    data.url = API_URL + data.url;
  }
  return data;
}

/**
 * Fetch ALL files (invoice image + bilagor) for a supplier invoice.
 * Returns { invoice_id, organization, files: [{ attachment_seq, label, available, content_type, url, ... }] }
 */
export async function getHogiaInvoiceFiles(invoiceId, { organization } = {}) {
  const qs = new URLSearchParams();
  if (organization) qs.set('organization', organization);
  const url = `${API_URL}/hogia/invoices/${invoiceId}/files${qs.toString() ? `?${qs.toString()}` : ''}`;
  const tenantSlug = getActiveTenantSlug();
  const headers = tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {};
  const res = await fetch(url, { credentials: 'include', headers });
  if (res.status === 404) {
    return { invoice_id: invoiceId, organization, files: [] };
  }
  if (!res.ok) {
    let msg = 'Failed to fetch invoice files';
    try {
      const data = await res.json();
      if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error;
    } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  const data = await res.json();
  // Ensure absolute URLs
  if (data?.files && Array.isArray(data.files)) {
    for (const f of data.files) {
      if (f && typeof f.url === 'string' && f.url.startsWith('/')) {
        f.url = API_URL + f.url;
      }
    }
  }
  return data;
}

// --- Hogia CSV exports ---
export function hogiaExportUrl(resource, params = {}) {
  const usp = new URLSearchParams();
  if (params.organization) usp.set('organization', params.organization);
  // optional filters by resource
  if (resource === 'invoices') {
    if (params.date_from) usp.set('date_from', params.date_from);
    if (params.date_to) usp.set('date_to', params.date_to);
    if (params.supplier_id) usp.set('supplier_id', params.supplier_id);
  }
  if (resource === 'vouchers') {
    if (params.date_from) usp.set('date_from', params.date_from);
    if (params.date_to) usp.set('date_to', params.date_to);
  }
  const base = `${API_URL}/hogia/export/${resource}`;
  return usp.toString() ? `${base}?${usp.toString()}` : base;
}

export async function hogiaBackfillAttachmentSizes({ organization, dry_run = false, limit = 1000 } = {}) {
  const usp = new URLSearchParams();
  if (organization && organization.trim()) usp.set('organization', organization.trim());
  if (dry_run != null) usp.set('dry_run', String(!!dry_run));
  if (limit != null) usp.set('limit', String(limit));
  const res = await fetch(`${API_URL}/hogia/admin/backfill-attachment-sizes?${usp.toString()}`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to backfill attachment sizes';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaAccounts(params = {}) {
  const usp = new URLSearchParams();
  if (params.organization && params.organization.trim()) usp.set('organization', params.organization.trim());
  if (params.q && params.q.trim()) usp.set('q', params.q.trim());
  if (params.sort_by) usp.set('sort_by', params.sort_by);
  if (params.sort_dir) usp.set('sort_dir', params.sort_dir);
  if (typeof params.page === 'number') usp.set('page', String(params.page)); else usp.set('page', '0');
  if (typeof params.page_size === 'number') usp.set('page_size', String(params.page_size));
  const res = await fetch(`${API_URL}/hogia/accounts?${usp.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to list Hogia accounts';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaSuppliers(params = {}) {
  const usp = new URLSearchParams();
  if (params.organization && params.organization.trim()) usp.set('organization', params.organization.trim());
  if (params.q && params.q.trim()) usp.set('q', params.q.trim());
  if (params.sort_by) usp.set('sort_by', params.sort_by);
  if (params.sort_dir) usp.set('sort_dir', params.sort_dir);
  if (typeof params.page === 'number') usp.set('page', String(params.page)); else usp.set('page', '0');
  if (typeof params.page_size === 'number') usp.set('page_size', String(params.page_size));
  const res = await fetch(`${API_URL}/hogia/suppliers?${usp.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to list Hogia suppliers';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaVouchers(params = {}) {
  const usp = new URLSearchParams();
  if (params.organization && params.organization.trim()) usp.set('organization', params.organization.trim());
  if (params.q && params.q.trim()) usp.set('q', params.q.trim());
  if (params.date_from) usp.set('date_from', params.date_from);
  if (params.date_to) usp.set('date_to', params.date_to);
  if (params.sort_by) usp.set('sort_by', params.sort_by);
  if (params.sort_dir) usp.set('sort_dir', params.sort_dir);
  if (typeof params.page === 'number') usp.set('page', String(params.page)); else usp.set('page', '0');
  if (typeof params.page_size === 'number') usp.set('page_size', String(params.page_size));
  const res = await fetch(`${API_URL}/hogia/vouchers?${usp.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to list Hogia vouchers';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaProjects(params = {}) {
  const usp = new URLSearchParams();
  if (params.organization && params.organization.trim()) usp.set('organization', params.organization.trim());
  if (params.q && params.q.trim()) usp.set('q', params.q.trim());
  if (typeof params.page === 'number') usp.set('page', String(params.page)); else usp.set('page', '0');
  if (typeof params.page_size === 'number') usp.set('page_size', String(params.page_size));
  const res = await fetch(`${API_URL}/hogia/projects?${usp.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to list Hogia projects';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaProfitcenters(params = {}) {
  const usp = new URLSearchParams();
  if (params.organization && params.organization.trim()) usp.set('organization', params.organization.trim());
  if (params.q && params.q.trim()) usp.set('q', params.q.trim());
  if (typeof params.page === 'number') usp.set('page', String(params.page)); else usp.set('page', '0');
  if (typeof params.page_size === 'number') usp.set('page_size', String(params.page_size));
  const res = await fetch(`${API_URL}/hogia/profitcenters?${usp.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to list Hogia profit centers';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaCostbearers(params = {}) {
  const usp = new URLSearchParams();
  if (params.organization && params.organization.trim()) usp.set('organization', params.organization.trim());
  if (params.q && params.q.trim()) usp.set('q', params.q.trim());
  if (typeof params.page === 'number') usp.set('page', String(params.page)); else usp.set('page', '0');
  if (typeof params.page_size === 'number') usp.set('page_size', String(params.page_size));
  const res = await fetch(`${API_URL}/hogia/costbearers?${usp.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to list Hogia cost bearers';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaCustomers(params = {}) {
  const usp = new URLSearchParams();
  if (params.organization && params.organization.trim()) usp.set('organization', params.organization.trim());
  if (params.q && params.q.trim()) usp.set('q', params.q.trim());
  if (typeof params.page === 'number') usp.set('page', String(params.page)); else usp.set('page', '0');
  if (typeof params.page_size === 'number') usp.set('page_size', String(params.page_size));
  const res = await fetch(`${API_URL}/hogia/customers?${usp.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to list Hogia customers';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaArticles(params = {}) {
  const usp = new URLSearchParams();
  if (params.organization && params.organization.trim()) usp.set('organization', params.organization.trim());
  if (params.q && params.q.trim()) usp.set('q', params.q.trim());
  if (typeof params.page === 'number') usp.set('page', String(params.page)); else usp.set('page', '0');
  if (typeof params.page_size === 'number') usp.set('page_size', String(params.page_size));
  const res = await fetch(`${API_URL}/hogia/articles?${usp.toString()}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to list Hogia articles';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); else if (data?.error) msg = data.error; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

// --- Hogia cross-reference: Project ↔ Suppliers ↔ Invoices ---

export async function listHogiaInvoicesByProject(projectNumber, { organization, page = 0, page_size = 50, status, q } = {}) {
  const usp = new URLSearchParams();
  if (organization && organization.trim()) usp.set('organization', organization.trim());
  usp.set('page', String(page));
  usp.set('page_size', String(page_size));
  // status: paid|unpaid|overdue|canceled. Filtreras i databasen over hela
  // projektet — klientsidig filtrering traffar bara den laddade sidan.
  if (status) usp.set('status', status);
  // q: fritext mot leverantorsnamn och fakturanummer, ocksa over hela projektet.
  if (q && q.trim()) usp.set('q', q.trim());
  const tenantSlug = getActiveTenantSlug();
  const headers = tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {};
  const res = await fetch(`${API_URL}/hogia/projects/${encodeURIComponent(projectNumber)}/invoices?${usp}`, { credentials: 'include', headers });
  if (!res.ok) {
    let msg = 'Failed to list invoices for project';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaSuppliersForProject(projectNumber, { organization } = {}) {
  const usp = new URLSearchParams();
  if (organization && organization.trim()) usp.set('organization', organization.trim());
  const tenantSlug = getActiveTenantSlug();
  const headers = tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {};
  const res = await fetch(`${API_URL}/hogia/projects/${encodeURIComponent(projectNumber)}/suppliers?${usp}`, { credentials: 'include', headers });
  if (!res.ok) {
    let msg = 'Failed to list suppliers for project';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

export async function listHogiaProjectsForSupplier(supplierId, { organization } = {}) {
  const usp = new URLSearchParams();
  if (organization && organization.trim()) usp.set('organization', organization.trim());
  const tenantSlug = getActiveTenantSlug();
  const headers = tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {};
  const res = await fetch(`${API_URL}/hogia/suppliers/${encodeURIComponent(supplierId)}/projects?${usp}`, { credentials: 'include', headers });
  if (!res.ok) {
    let msg = 'Failed to list projects for supplier';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return await res.json();
}

// --- Hogia health/status/monitor ---
export async function getHogiaHealth() {
  const tenantSlug = getActiveTenantSlug();
  const headers = tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {};
  const res = await fetch(`${API_URL}/hogia/health`, { credentials: 'include', headers });
  if (!res.ok) throw new Error('Failed to fetch Hogia health');
  return await res.json();
}

export async function getHogiaSyncStatus({ organization } = {}) {
  const usp = new URLSearchParams();
  if (organization && organization.trim()) usp.set('organization', organization.trim());
  const url = `${API_URL}/hogia/sync/status${usp.toString() ? `?${usp.toString()}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    let msg = 'Failed to fetch Hogia sync status';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function hogiaMonitorCheckpoints({ organization, stale_after_hours = 24, max_pages = 1, page_size = 50, include_invoices = true, include_vouchers = true } = {}) {
  const body = {
    organization: organization || null,
    stale_after_hours,
    max_pages,
    page_size,
    include_invoices,
    include_vouchers,
  };
  const res = await fetch(`${API_URL}/hogia/admin/monitor-checkpoints`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    let msg = 'Failed to run Hogia monitor';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch { try { msg = await res.text(); } catch {} }
    throw new Error(msg);
  }
  return await res.json();
}

// --- Hogia → KanPlan project sync ---

/** List Hogia projects available for syncing into KanPlan. */
export async function getHogiaProjectsAvailable({ q, page = 0, page_size = 200 } = {}) {
  const usp = new URLSearchParams();
  if (q) usp.set('q', q.trim());
  usp.set('page', String(page));
  usp.set('page_size', String(page_size));
  const res = await apiFetch(`${API_URL}/projects/hogia-available?${usp.toString()}`);
  if (!res.ok) {
    let msg = 'Kunde inte hämta Hogia-projekt';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

/** Sync selected Hogia projects (from staging) into KanPlan projects table. */
export async function syncProjectsFromHogia({ project_numbers, sync_all = false } = {}) {
  const body = { sync_all };
  if (project_numbers && project_numbers.length > 0) {
    body.project_numbers = project_numbers;
  }
  const res = await apiFetch(`${API_URL}/projects/sync-from-hogia`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = 'Kunde inte synka projekt från Hogia';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

// ─── Project Organisation Plans ──────────────────────────────

export async function fetchProjectOrgPlans(projectId) {
  const res = await apiFetch(`${API_URL}/project-orgplans/?project_id=${projectId}`);
  if (!res.ok) throw new Error('Kunde inte hämta organisationsplaner');
  return await res.json();
}

export async function fetchProjectOrgPlan(planId) {
  const res = await apiFetch(`${API_URL}/project-orgplans/${planId}`);
  if (!res.ok) throw new Error('Kunde inte hämta organisationsplan');
  return await res.json();
}

export async function createProjectOrgPlan(data) {
  const res = await apiFetch(`${API_URL}/project-orgplans/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let msg = 'Kunde inte skapa organisationsplan';
    try { const d = await res.json(); if (d?.detail) msg = d.detail; } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function updateProjectOrgPlan(planId, data) {
  const res = await apiFetch(`${API_URL}/project-orgplans/${planId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Kunde inte spara organisationsplan');
  return await res.json();
}

export async function deleteProjectOrgPlan(planId) {
  const res = await apiFetch(`${API_URL}/project-orgplans/${planId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Kunde inte radera organisationsplan');
  return await res.json();
}

export async function fetchOrgPlanResources() {
  const res = await apiFetch(`${API_URL}/project-orgplans/resources/available`);
  if (!res.ok) return [];
  return await res.json();
}

export async function previewOrgPlanPdf(data) {
  const res = await apiFetch(`${API_URL}/project-orgplans/pdf/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Kunde inte generera PDF');
  return await res.blob();
}

// ── Mängdavstämning (Quantity Reconciliation) ────────────────────────

export async function fetchQuantitySchedules(projectId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules`);
  if (!res.ok) return [];
  return await res.json();
}

export async function createQuantitySchedule(projectId, data) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Kunde inte skapa mängdförteckning');
  return await res.json();
}

export async function updateQuantitySchedule(projectId, scheduleId, data) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Kunde inte uppdatera mängdförteckning');
  return await res.json();
}

export async function deleteQuantitySchedule(projectId, scheduleId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Kunde inte radera mängdförteckning');
}

export async function fetchScheduleItems(projectId, scheduleId, { includeInactive = false } = {}) {
  const params = new URLSearchParams();
  if (includeInactive) params.set('include_inactive', 'true');
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/items?${params}`);
  if (!res.ok) return [];
  return await res.json();
}

export async function createScheduleItem(projectId, scheduleId, data) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Kunde inte skapa post');
  return await res.json();
}

export async function batchCreateScheduleItems(projectId, scheduleId, items) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/items/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) throw new Error('Kunde inte importera poster');
  return await res.json();
}

export async function updateScheduleItem(projectId, scheduleId, itemId, data) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/items/${itemId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Kunde inte uppdatera post');
  return await res.json();
}

export async function batchUpdateForecast(projectId, scheduleId, updates) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/items/batch-forecast`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates }),
  });
  if (!res.ok) throw new Error('Kunde inte uppdatera prognoser');
  return await res.json();
}

export async function fetchWithdrawals(projectId, { scheduleId } = {}) {
  const params = new URLSearchParams();
  if (scheduleId) params.set('schedule_id', scheduleId);
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals?${params}`);
  if (!res.ok) return [];
  return await res.json();
}

export async function createWithdrawal(projectId, data) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Kunde inte skapa uttag');
  return await res.json();
}

export async function updateWithdrawal(projectId, withdrawalId, data) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Kunde inte uppdatera uttag');
  return await res.json();
}

export async function signWithdrawal(projectId, withdrawalId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/sign`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error('Kunde inte signera uttag');
  return await res.json();
}

export async function fetchWithdrawalLines(projectId, withdrawalId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/lines`);
  if (!res.ok) return [];
  return await res.json();
}

export async function upsertWithdrawalLines(projectId, withdrawalId, lines) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/lines`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(lines),
  });
  if (!res.ok) throw new Error('Kunde inte spara uttagsrader');
  return await res.json();
}

export async function fetchQuantityKPI(projectId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/kpi`);
  if (!res.ok) return null;
  return await res.json();
}

export async function fetchQuantityTimeSeries(projectId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/timeseries`);
  if (!res.ok) return [];
  return await res.json();
}

export async function importScheduleFromCalc(projectId, scheduleId, calcVersionId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/import-from-calc`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ calc_version_id: calcVersionId }),
  });
  if (!res.ok) {
    let msg = 'Kunde inte importera från kalkyl';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function fetchItemHistory(projectId, scheduleId, itemId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/items/${itemId}/history`);
  if (!res.ok) return [];
  return await res.json();
}

export async function fetchItemWithdrawalLines(projectId, scheduleId, itemId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/items/${itemId}/withdrawal-lines`);
  if (!res.ok) return [];
  return await res.json();
}

export async function fetchAtaItems(projectId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/ata-items`);
  if (!res.ok) return [];
  return await res.json();
}

export function getSchedulePdfUrl(projectId, scheduleId) {
  return `${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/export-pdf`;
}

export function getWithdrawalPdfUrl(projectId, withdrawalId) {
  return `${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/export-pdf`;
}

export function getAcontoPdfUrl(projectId, withdrawalId) {
  return `${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/export-aconto-pdf`;
}

export function getForecastPdfUrl(projectId) {
  return `${API_URL}/projects/${projectId}/mangdavstamning/export-pdf/forecast`;
}

/* ── Withdrawal submit / review (S3) ── */
export async function submitWithdrawal(projectId, withdrawalId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/submit`, { method: 'POST' });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
}
export async function reviewWithdrawal(projectId, withdrawalId, body) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/review`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
}

/* ── Per-line review (Gap 1+2) ── */
export async function reviewWithdrawalLine(projectId, withdrawalId, lineId, body) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/lines/${lineId}/review`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
}

export async function batchReviewWithdrawalLines(projectId, withdrawalId, lineReviews) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/lines/batch-review`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ line_reviews: lineReviews }),
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
}

export async function respondToLineReview(projectId, withdrawalId, lineId, reviewResponse) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/lines/${lineId}/respond`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ review_response: reviewResponse }),
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
}

/* ── External portal (S3) ── */
export async function fetchExternalMangdInfo(token) {
  const res = await fetch(`${API_URL}/mangdavstamning/external/${token}/info`);
  if (!res.ok) throw new Error('Token ogiltig');
  return await res.json();
}
export async function fetchExternalSchedules(token) {
  const res = await fetch(`${API_URL}/mangdavstamning/external/${token}/schedules`);
  if (!res.ok) return [];
  return await res.json();
}
export async function fetchExternalItems(token, scheduleId) {
  const res = await fetch(`${API_URL}/mangdavstamning/external/${token}/schedules/${scheduleId}/items`);
  if (!res.ok) return [];
  return await res.json();
}
export async function fetchExternalWithdrawals(token) {
  const res = await fetch(`${API_URL}/mangdavstamning/external/${token}/withdrawals`);
  if (!res.ok) return [];
  return await res.json();
}
export async function fetchExternalWithdrawalLines(token, withdrawalId) {
  const res = await fetch(`${API_URL}/mangdavstamning/external/${token}/withdrawals/${withdrawalId}/lines`);
  if (!res.ok) return [];
  return await res.json();
}
export async function submitExternalReview(token, withdrawalId, body) {
  const res = await fetch(`${API_URL}/mangdavstamning/external/${token}/withdrawals/${withdrawalId}/review`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
}
export async function submitExternalLineReview(token, withdrawalId, lineId, body) {
  const res = await fetch(`${API_URL}/mangdavstamning/external/${token}/withdrawals/${withdrawalId}/lines/${lineId}/review`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
}

/* ── Item attachments (S4) ── */
export async function fetchItemAttachments(projectId, itemId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/items/${itemId}/attachments`);
  if (!res.ok) return [];
  return await res.json();
}
export async function uploadItemAttachment(projectId, itemId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/items/${itemId}/attachments`, { method: 'POST', body: formData });
  if (!res.ok) throw new Error('Upload failed');
  return await res.json();
}
export function getAttachmentDownloadUrl(projectId, attachmentId) {
  return `${API_URL}/projects/${projectId}/mangdavstamning/attachments/${attachmentId}/download`;
}
export async function deleteItemAttachment(projectId, attachmentId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/attachments/${attachmentId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Delete failed');
  return await res.json();
}

/* ── AMA quick-links (S4) ── */
export async function fetchItemAmaInfo(projectId, itemId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/items/${itemId}/ama-info`);
  if (!res.ok) return null;
  return await res.json();
}

/* ── S5: KanAI forecast suggestion ── */
export async function suggestForecast(projectId, itemId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/items/${itemId}/suggest-forecast`, { method: 'POST' });
  if (!res.ok) throw new Error('Kunde inte hämta prognosförslag');
  return await res.json();
}

/* ── S5: View settings ── */
export async function fetchViewSettings(projectId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/view-settings`);
  if (!res.ok) return {};
  const data = await res.json();
  return data.settings || {};
}

export async function saveViewSettings(projectId, settings) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/view-settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ settings }),
  });
  if (!res.ok) throw new Error('Kunde inte spara vyinställningar');
  return await res.json();
}

/* ── S6: Invoice status flow ── */
export async function submitWithdrawalForBilling(projectId, withdrawalId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/submit-for-billing`, { method: 'POST' });
  if (!res.ok) throw new Error('Kunde inte skicka för fakturering');
  return await res.json();
}

export async function approveBilling(projectId, withdrawalId, body) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/approve-billing`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Kunde inte godkänna/avvisa faktureringsunderlag');
  return await res.json();
}

export async function markWithdrawalInvoiced(projectId, withdrawalId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/withdrawals/${withdrawalId}/mark-invoiced`, { method: 'POST' });
  if (!res.ok) throw new Error('Kunde inte markera som fakturerad');
  return await res.json();
}

/* ── Mass Balance (Massbalans) ── */
export async function fetchMassBalanceEntries(projectId, material) {
  const url = material
    ? `${API_URL}/projects/${projectId}/mangdavstamning/mass-balance?material=${encodeURIComponent(material)}`
    : `${API_URL}/projects/${projectId}/mangdavstamning/mass-balance`;
  const res = await apiFetch(url);
  if (!res.ok) return [];
  return await res.json();
}

export async function fetchMassBalanceSummary(projectId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/mass-balance/summary`);
  if (!res.ok) return [];
  return await res.json();
}

export async function createMassBalanceEntry(projectId, body) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/mass-balance`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Kunde inte skapa massbalanspost');
  return await res.json();
}

export async function deleteMassBalanceEntry(projectId, entryId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/mass-balance/${entryId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Kunde inte ta bort massbalanspost');
}

/* ── S6: Drawing links ── */
export async function linkDrawingToItem(projectId, itemId, drawingId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/items/${itemId}/link-drawing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ drawing_id: drawingId }),
  });
  if (!res.ok) throw new Error('Kunde inte länka ritning');
  return await res.json();
}

export async function unlinkDrawingFromItem(projectId, itemId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/items/${itemId}/link-drawing`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Kunde inte ta bort ritningslänk');
}

/* ── S3: External token management ── */
export async function listMangdExtTokens(projectId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/external-tokens`);
  if (!res.ok) throw new Error('Kunde inte hämta externa tokens');
  return await res.json();
}

export async function createMangdExtToken(projectId, label, expiresInDays, kind = 'read') {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/external-tokens`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ project_id: projectId, label, expires_in_days: expiresInDays, kind }),
  });
  if (!res.ok) throw new Error('Kunde inte skapa extern token');
  return await res.json();
}

export async function revokeMangdExtToken(projectId, tokenId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/external-tokens/${tokenId}/revoke`, { method: 'POST' });
  if (!res.ok) throw new Error('Kunde inte återkalla token');
  return await res.json();
}

export async function deleteMangdExtToken(projectId, tokenId) {
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/external-tokens/${tokenId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Kunde inte radera token');
}

/**
 * Upload a BoQ file (Excel/CSV/PDF) for server-side parsing.
 * Returns { filename, file_type, sheets: [{sheet_name, headers, rows, row_count}], detected_mapping }
 */
export async function parseBoqFile(projectId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/parse-boq-file`, {
    method: 'POST',
    body: formData,
    // NOTE: Do NOT set Content-Type — browser sets multipart boundary automatically
  });
  if (!res.ok) {
    let msg = 'Kunde inte tolka filen';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function parsePdfParserPreview(projectId, scheduleId, file, ocrMode = false, maxPreviewRows = 50) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('ocr_mode', ocrMode ? 'true' : 'false');
  formData.append('max_preview_rows', String(maxPreviewRows));
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/parse-pdf-parser-preview`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    let msg = 'Kunde inte analysera PDF';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function importScheduleFromPdfParser(projectId, scheduleId, file, ocrMode = false) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('ocr_mode', ocrMode ? 'true' : 'false');
  const res = await apiFetch(`${API_URL}/projects/${projectId}/mangdavstamning/schedules/${scheduleId}/import-from-pdf-parser`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    let msg = 'Kunde inte importera PDF till mängdförteckning';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function analyzeMangdPdfTool(file, ocrMode = false, maxPreviewRows = 50) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('ocr_mode', ocrMode ? 'true' : 'false');
  formData.append('max_preview_rows', String(maxPreviewRows));
  const res = await apiFetch(`${API_URL}/api/tools/mangd-parser/analyze`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    let msg = 'Kunde inte analysera PDF';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  return await res.json();
}

export async function downloadMangdPdfToolCsv(file, ocrMode = false, pricingTemplate = false) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('ocr_mode', ocrMode ? 'true' : 'false');
  const endpoint = pricingTemplate ? 'pricing-template' : 'csv';
  const res = await apiFetch(`${API_URL}/api/tools/mangd-parser/export/${endpoint}`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    let msg = 'Kunde inte exportera CSV';
    try { const data = await res.json(); if (data?.detail) msg = formatApiErrorDetail(data.detail); } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const header = res.headers.get('content-disposition') || '';
  const match = /filename=([^;]+)/i.exec(header);
  const filename = match ? match[1].replace(/"/g, '').trim() : (pricingTemplate ? 'mangd_prissattning.csv' : 'mangd.csv');
  return { blob, filename };
}

export async function listConstructionInvoices(projectId) {
  const res = await apiFetch(`${API_URL}/construction-invoices/project/${projectId}`);
  if (!res.ok) return [];
  return await res.json();
}

// --- Dagbok ↔ ÄTA-koppling (sökning + manuell länkning) ---

export async function searchDiaryAtaMentions(projectId, { keywords, q, onlyUnlinked } = {}) {
  const params = new URLSearchParams();
  if (onlyUnlinked) params.append("only_unlinked", "true");
  if (q && q.trim()) params.append("q", q.trim());
  if (Array.isArray(keywords)) {
    keywords.forEach((kw) => kw && params.append("keywords", kw));
  }
  const qs = params.toString();
  const url = `${API_URL}/changes/project/${projectId}/diary/ata-mentions${qs ? `?${qs}` : ""}`;
  const res = await apiFetch(url);
  if (!res.ok) throw new Error(`Sökning misslyckades (${res.status})`);
  return await res.json();
}

export async function createDiaryAtaLink(body) {
  const res = await apiFetch(`${API_URL}/changes/diary-links`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j && j.detail) detail = j.detail;
    } catch {}
    throw new Error(detail);
  }
  return await res.json();
}

export async function deleteDiaryAtaLink(linkId) {
  const res = await apiFetch(`${API_URL}/changes/diary-links/${linkId}`, { method: "DELETE" });
  if (!res.ok && res.status !== 204) throw new Error(`Kunde inte ta bort länken (${res.status})`);
  return true;
}

export async function listDiaryLinksForChange(changeId) {
  const res = await apiFetch(`${API_URL}/changes/${changeId}/diary-links`);
  if (!res.ok) return [];
  return await res.json();
}

// --- KanAI ATA-suggestions (Lager 2) ---

export async function listKanaiAtaSuggestions(projectId) {
  const res = await apiFetch(`${API_URL}/changes/project/${projectId}/kanai-ata-suggestions`);
  if (!res.ok) return [];
  return await res.json();
}

export async function acceptKanaiAtaSuggestion(suggestionId, overrides = {}) {
  const res = await apiFetch(`${API_URL}/changes/kanai-ata-suggestions/${suggestionId}/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(overrides || {}),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j && j.detail) detail = j.detail;
    } catch {}
    throw new Error(detail);
  }
  return await res.json();
}

export async function dismissKanaiAtaSuggestion(suggestionId) {
  const res = await apiFetch(`${API_URL}/changes/kanai-ata-suggestions/${suggestionId}/dismiss`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`Kunde inte avvisa förslaget (${res.status})`);
  return await res.json();
}

export async function getKanaiUsageSummary({ fromDate, toDate, groupBy = "day" } = {}) {
  const params = new URLSearchParams();
  if (fromDate) params.append("from", fromDate);
  if (toDate) params.append("to", toDate);
  params.append("group_by", groupBy);
  const res = await apiFetch(`${API_URL}/api/kanai/usage/summary?${params.toString()}`);
  if (!res.ok) throw new Error(`Kunde inte hämta usage (${res.status})`);
  return await res.json();
}
